import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, Routes, Route, useLocation, useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import BLOG_DATA from './blogData';
import { supabase } from './supabaseClient';
import { Lock, Unlock, Menu, X, Phone, MapPin, Clock, Award, ShoppingBag, GraduationCap, Mail, Sun, Sparkles, Calendar as CalendarIcon, Users, DollarSign, Heart, Palette, PencilLine, BookOpen, Leaf, Feather, Star, Cloud, Map as MapIcon, Smile, TreePine, Printer, Wrench, Lightbulb, Boxes } from 'lucide-react';
import theme from './theme';
import './App.css';

// After this date the Summer Adventure Journal Club has ended — the banner
// and signup CTA automatically swap to a recap + waitlist view. Uses local
// time (JS Date month is 0-indexed, so 7 = August).
const CAMP_END_DATE = new Date(2026, 7, 8);
const isPostCamp = () => new Date() >= CAMP_END_DATE;

const IgIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect width="20" height="20" x="2" y="2" rx="5" ry="5" />
    <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
    <line x1="17.5" x2="17.51" y1="6.5" y2="6.5" />
  </svg>
);

const FbIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
  </svg>
);

// ─── Time formatting helper (shared) ──────────────────────
// Converts "18:00" or "18:00:00" → "6:00 PM"
const formatTime12h = (t) => {
  if (!t) return '';
  const [h, m] = t.slice(0, 5).split(':');
  const hour = parseInt(h, 10);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const h12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  return `${h12}:${m} ${ampm}`;
};

// Format an ISO timestamp to a relative-friendly local string
const formatAuditTime = (ts) => {
  if (!ts) return '';
  const d = new Date(ts);
  return d.toLocaleString(undefined, {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit'
  });
};

// ─── Scroll To Top on Route Change ──────────────────────
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

// ─── Photo Grid ───────────────────────────────────────────
function PhotoGrid({ photos, isMobile }) {
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: isMobile ? 'repeat(auto-fill, minmax(250px, 1fr))' : 'repeat(auto-fill, minmax(300px, 1fr))',
      gap: '16px'
    }}>
      {photos.map((photo, i) => (
        <div key={i} style={{
          borderRadius: '14px',
          overflow: 'hidden',
          border: '1px solid #eee',
          backgroundColor: '#fff',
          transition: 'transform 0.2s, box-shadow 0.2s',
          cursor: 'pointer'
        }}
        onMouseEnter={e => {
          e.currentTarget.style.transform = 'translateY(-4px)';
          e.currentTarget.style.boxShadow = '0 12px 32px rgba(0,0,0,0.1)';
        }}
        onMouseLeave={e => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = 'none';
        }}
        >
          <img
            src={photo.src}
            alt={photo.alt}
            style={{
              width: '100%',
              height: '240px',
              objectFit: 'cover',
              display: 'block'
            }}
          />
          {photo.caption && (
            <div style={{ padding: '14px 16px' }}>
              <p style={{
                fontSize: '0.85rem',
                fontWeight: '600',
                color: '#444',
                margin: 0
              }}>
                {photo.caption}
              </p>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ─── Section Header ───────────────────────────────────────
function SectionHeader({ title, subtitle }) {
  return (
    <>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        marginBottom: '8px'
      }}>
        <div style={{
          width: '4px',
          height: '28px',
          backgroundColor: theme.primary,
          borderRadius: '2px'
        }} />
        <h2 style={{
          fontSize: '1.4rem',
          fontWeight: '800',
          color: theme.primary,
          margin: 0,
          letterSpacing: '0.06em',
          textTransform: 'uppercase'
        }}>
          {title}
        </h2>
      </div>
      {subtitle && (
        <p style={{ color: '#999', fontSize: '0.85rem', marginBottom: '28px', marginLeft: '16px', marginTop: '4px' }}>
          {subtitle}
        </p>
      )}
    </>
  );
}


// ─── Calendar Component ───────────────────────────────────
// ─── Staff Login Modal ────────────────────────────────────
function StaffLogin({ onClose, onLogin }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) {
      setError(error.message);
      setLoading(false);
    } else {
      onLogin();
      onClose();
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '24px'
    }} onClick={onClose}>
      <div style={{
        backgroundColor: '#fff', borderRadius: '16px', padding: '32px',
        width: '100%', maxWidth: '380px', boxShadow: '0 20px 60px rgba(0,0,0,0.2)'
      }} onClick={e => e.stopPropagation()}>
        <h2 style={{ fontSize: '1.3rem', fontWeight: '800', color: '#1a1a1a', margin: '0 0 4px 0' }}>Staff Login</h2>
        <p style={{ fontSize: '0.8rem', color: '#999', margin: '0 0 20px 0' }}>Poke Pine Cabin staff only</p>
        <form onSubmit={handleLogin}>
          <input type="email" placeholder="Email" value={email} onChange={e => setEmail(e.target.value)}
            style={{
              width: '100%', padding: '12px 14px', fontSize: '0.9rem', border: '1px solid #ddd',
              borderRadius: '8px', marginBottom: '10px', boxSizing: 'border-box', outline: 'none'
            }}
          />
          <input type="password" placeholder="Password" value={password} onChange={e => setPassword(e.target.value)}
            style={{
              width: '100%', padding: '12px 14px', fontSize: '0.9rem', border: '1px solid #ddd',
              borderRadius: '8px', marginBottom: '16px', boxSizing: 'border-box', outline: 'none'
            }}
          />
          {error && <p style={{ color: theme.primary, fontSize: '0.8rem', margin: '0 0 12px 0' }}>{error}</p>}
          <button type="submit" disabled={loading} style={{
            width: '100%', padding: '12px', backgroundColor: theme.primary, color: '#fff',
            border: 'none', borderRadius: '8px', fontWeight: '700', fontSize: '0.9rem',
            cursor: loading ? 'default' : 'pointer', opacity: loading ? 0.7 : 1
          }}>
            {loading ? 'Logging in...' : 'Log In'}
          </button>
        </form>
      </div>
    </div>
  );
}

// ─── Category Colors ──────────────────────────────────────
const CATEGORIES = {
  trade_night: { label: 'Trade Night', color: theme.primary },
  tournament: { label: 'Tournament', color: '#2563eb' },
  big_event: { label: 'Big Event', color: '#7c3aed' },
  other: { label: 'Other', color: '#ea580c' }
};

// ─── Event Modal (Add/Edit) ───────────────────────────────
function EventModal({ date, existingEvents, onClose, onSave, onDelete, isMobile, staff }) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [startTime, setStartTime] = useState('18:00');
  const [endTime, setEndTime] = useState('20:00');
  const [location, setLocation] = useState('');
  const [category, setCategory] = useState('other');
  const [recurrence, setRecurrence] = useState('none');
  const [recurrenceEndDate, setRecurrenceEndDate] = useState('');
  const [editingEvent, setEditingEvent] = useState(null);

  const dateStr = `${date.toLocaleString('default', { month: 'long' })} ${date.getDate()}, ${date.getFullYear()}`;

  const resetForm = () => {
    setTitle(''); setDescription(''); setStartTime('18:00'); setEndTime('20:00');
    setLocation(''); setCategory('other'); setRecurrence('none'); setRecurrenceEndDate(''); setEditingEvent(null);
  };

  const loadEvent = (event) => {
    setEditingEvent(event);
    setTitle(event.title);
    setDescription(event.description || '');
    setStartTime(event.start_time?.slice(0, 5) || '18:00');
    setEndTime(event.end_time?.slice(0, 5) || '20:00');
    setLocation(event.location || '');
    setCategory(event.category || 'other');
    setRecurrence(event.recurrence || 'none');
    setRecurrenceEndDate(event.recurrence_end_date || '');
  };

  const clearRecurrence = () => {
    setRecurrence('none');
    setRecurrenceEndDate('');
  };

  const handleSave = () => {
    if (!title.trim()) return;
    const dateFormatted = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    const eventData = {
      title: title.trim(),
      description: description.trim() || null,
      event_date: dateFormatted,
      start_time: startTime,
      end_time: endTime,
      location: location.trim() || null,
      category,
      recurrence,
      recurrence_end_date: recurrence !== 'none' && recurrenceEndDate ? recurrenceEndDate : null
    };
    if (editingEvent?.id) {
      eventData.id = editingEvent.id;
      eventData.updated_by = staff?.id || null;
      eventData.updated_by_name = staff?.name || null;
    } else {
      eventData.created_by = staff?.id || null;
      eventData.created_by_name = staff?.name || null;
    }
    onSave(eventData);
    resetForm();
  };

  // Inputs use 16px font on mobile to prevent iOS Safari's zoom-on-focus.
  const inputStyle = {
    width: '100%', padding: '12px 14px', fontSize: '16px', border: '1px solid #ddd',
    borderRadius: '8px', marginBottom: '10px', boxSizing: 'border-box', outline: 'none',
    fontFamily: 'inherit'
  };

  const textareaStyle = {
    ...inputStyle,
    minHeight: '90px',
    resize: 'vertical',
    lineHeight: '1.4'
  };

  // On mobile, the modal becomes a full-screen sheet. On desktop it's a centered card.
  const overlayStyle = {
    position: 'fixed', inset: 0, zIndex: 9999,
    backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: isMobile ? 'stretch' : 'center',
    justifyContent: 'center',
    padding: isMobile ? '0' : '24px'
  };

  const sheetStyle = isMobile
    ? {
        backgroundColor: '#fff',
        width: '100%',
        height: '100%',
        padding: '20px',
        paddingBottom: '40px',
        overflowY: 'auto',
        WebkitOverflowScrolling: 'touch'
      }
    : {
        backgroundColor: '#fff', borderRadius: '16px', padding: '28px',
        width: '100%', maxWidth: '480px', boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
        maxHeight: '90vh', overflowY: 'auto'
      };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={sheetStyle} onClick={e => e.stopPropagation()}>
        {/* Header with close button on mobile */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '12px' }}>
          <div>
            <h2 style={{ fontSize: '1.2rem', fontWeight: '800', color: '#1a1a1a', margin: '0 0 2px 0' }}>{dateStr}</h2>
            <p style={{ fontSize: '0.8rem', color: '#999', margin: 0 }}>
              {editingEvent ? 'Editing event' : 'Add a new event'}
            </p>
          </div>
          {isMobile && (
            <button onClick={onClose} style={{
              background: '#f0f0f0', border: 'none', borderRadius: '50%',
              width: '36px', height: '36px', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0
            }} aria-label="Close">
              <X size={18} />
            </button>
          )}
        </div>

        {/* Audit info — only when editing */}
        {editingEvent && (editingEvent.created_by_name || editingEvent.updated_by_name) && (
          <div style={{
            backgroundColor: '#fafafa', border: '1px solid #eee', borderRadius: '8px',
            padding: '8px 12px', marginBottom: '12px', fontSize: '0.75rem', color: '#666',
            lineHeight: '1.5'
          }}>
            {editingEvent.created_by_name && (
              <div>Created by <strong>{editingEvent.created_by_name}</strong>{editingEvent.created_at ? ` · ${formatAuditTime(editingEvent.created_at)}` : ''}</div>
            )}
            {editingEvent.updated_by_name && (
              <div>Last edited by <strong>{editingEvent.updated_by_name}</strong>{editingEvent.updated_at ? ` · ${formatAuditTime(editingEvent.updated_at)}` : ''}</div>
            )}
          </div>
        )}

        {/* Existing events on this day */}
        {existingEvents.length > 0 && !editingEvent && (
          <div style={{ marginBottom: '16px' }}>
            {existingEvents.map(ev => (
              <div key={ev.id} style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '10px 12px', backgroundColor: '#f8f8f8', borderRadius: '8px',
                marginBottom: '6px', border: '1px solid #eee'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0, flex: 1 }}>
                  <div style={{
                    width: '8px', height: '8px', borderRadius: '50%', flexShrink: 0,
                    backgroundColor: CATEGORIES[ev.category]?.color || '#ea580c'
                  }} />
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontWeight: '700', fontSize: '0.85rem', color: '#1a1a1a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ev.title}</div>
                    <div style={{ fontSize: '0.75rem', color: '#888' }}>
                      {formatTime12h(ev.start_time)} - {formatTime12h(ev.end_time)}
                      {ev.recurrence !== 'none' && <span style={{ color: theme.primary, marginLeft: '8px' }}>{ev.recurrence}</span>}
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                  <button onClick={() => loadEvent(ev)} style={{
                    background: '#eee', border: 'none', borderRadius: '6px', padding: '6px 10px',
                    fontSize: '0.75rem', fontWeight: '600', cursor: 'pointer'
                  }}>Edit</button>
                  <button onClick={() => onDelete(ev.id)} style={{
                    background: theme.primaryBg, border: 'none', borderRadius: '6px', padding: '6px 10px',
                    fontSize: '0.75rem', fontWeight: '600', cursor: 'pointer', color: theme.primary
                  }}>Delete</button>
                </div>
              </div>
            ))}
            <div style={{ borderBottom: '1px solid #eee', margin: '12px 0' }} />
          </div>
        )}

        <label style={{ fontSize: '0.7rem', color: '#999', fontWeight: '600' }}>Title</label>
        <input placeholder="Event title" value={title} onChange={e => setTitle(e.target.value)} style={inputStyle} />

        <label style={{ fontSize: '0.7rem', color: '#999', fontWeight: '600' }}>Description (optional)</label>
        <textarea
          placeholder="Use **bold** or *italic* for emphasis. Press Enter for new lines."
          value={description}
          onChange={e => setDescription(e.target.value)}
          style={textareaStyle}
        />
        <div style={{ fontSize: '0.7rem', color: '#aaa', margin: '-4px 0 12px 2px' }}>
          Markdown supported: <strong>**bold**</strong> · <em>*italic*</em>
        </div>

        <div style={{ display: 'flex', gap: '8px' }}>
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: '0.7rem', color: '#999', fontWeight: '600' }}>Start</label>
            <input type="time" value={startTime} onChange={e => setStartTime(e.target.value)} style={inputStyle} />
          </div>
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: '0.7rem', color: '#999', fontWeight: '600' }}>End</label>
            <input type="time" value={endTime} onChange={e => setEndTime(e.target.value)} style={inputStyle} />
          </div>
        </div>

        <label style={{ fontSize: '0.7rem', color: '#999', fontWeight: '600' }}>Location (optional)</label>
        <input placeholder="Location" value={location} onChange={e => setLocation(e.target.value)} style={inputStyle} />

        <label style={{ fontSize: '0.7rem', color: '#999', fontWeight: '600' }}>Category</label>
        <select value={category} onChange={e => setCategory(e.target.value)} style={{ ...inputStyle, cursor: 'pointer' }}>
          {Object.entries(CATEGORIES).map(([key, { label }]) => (
            <option key={key} value={key}>{label}</option>
          ))}
        </select>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '4px' }}>
          <label style={{ fontSize: '0.7rem', color: '#999', fontWeight: '600' }}>Repeat</label>
          {recurrence !== 'none' && (
            <button
              type="button"
              onClick={clearRecurrence}
              style={{
                background: 'none', border: 'none', color: theme.primary,
                fontSize: '0.7rem', fontWeight: '700', cursor: 'pointer', padding: '4px 8px'
              }}
            >
              Clear repeat
            </button>
          )}
        </div>
        <select value={recurrence} onChange={e => setRecurrence(e.target.value)} style={{ ...inputStyle, cursor: 'pointer' }}>
          <option value="none">Does not repeat</option>
          <option value="weekly">Every week</option>
          <option value="biweekly">Every 2 weeks</option>
          <option value="monthly">Every month</option>
        </select>

        {recurrence !== 'none' && (
          <>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <label style={{ fontSize: '0.7rem', color: '#999', fontWeight: '600' }}>Repeat until (optional)</label>
              {recurrenceEndDate && (
                <button
                  type="button"
                  onClick={() => setRecurrenceEndDate('')}
                  style={{
                    background: 'none', border: 'none', color: theme.primary,
                    fontSize: '0.7rem', fontWeight: '700', cursor: 'pointer', padding: '4px 8px'
                  }}
                >
                  Clear end date
                </button>
              )}
            </div>
            <input type="date" value={recurrenceEndDate} onChange={e => setRecurrenceEndDate(e.target.value)} style={inputStyle} />
          </>
        )}

        <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
          <button onClick={handleSave} style={{
            flex: 1, padding: '14px', backgroundColor: theme.primary, color: '#fff',
            border: 'none', borderRadius: '8px', fontWeight: '700', fontSize: '0.95rem', cursor: 'pointer'
          }}>
            {editingEvent ? 'Update Event' : 'Add Event'}
          </button>
          {editingEvent && (
            <button onClick={resetForm} style={{
              padding: '14px 18px', backgroundColor: '#f0f0f0', color: '#666',
              border: 'none', borderRadius: '8px', fontWeight: '600', fontSize: '0.9rem', cursor: 'pointer'
            }}>
              Cancel
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Calendar Component ───────────────────────────────────
function Calendar({ isStaff, isMobile, staff, categoryFilter, calendarRef, events, fetchEvents }) {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState(null);
  const [showEventModal, setShowEventModal] = useState(false);
  const [modalDate, setModalDate] = useState(null);
  const detailPanelRef = useRef(null);

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const monthName = currentDate.toLocaleString('default', { month: 'long' });
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const today = new Date();
  const isCurrentMonth = today.getFullYear() === year && today.getMonth() === month;
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const prevMonth = () => { setCurrentDate(new Date(year, month - 1, 1)); setSelectedDay(null); };
  const nextMonth = () => { setCurrentDate(new Date(year, month + 1, 1)); setSelectedDay(null); };

  // Expose current year/month on the calendarRef for the print function
  useEffect(() => {
    if (calendarRef?.current) {
      calendarRef.current.dataset.year = year;
      calendarRef.current.dataset.month = month;
    }
  }, [year, month, calendarRef]);

  // On mobile, when a public visitor (non-staff) selects a day, scroll the
  // detail panel into view so they don't miss the events that appeared below.
  // Staff are excluded — they have their own add/edit flow and the auto-scroll
  // would fight with the modal.
  useEffect(() => {
    if (selectedDay !== null && isMobile && !isStaff && detailPanelRef.current) {
      detailPanelRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [selectedDay, isMobile, isStaff]);

  const getEventsForDay = (day) => {
    const dateObj = new Date(year, month, day);
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

    return events.filter(ev => {
      if (ev.event_date === dateStr) return true;
      if (ev.recurrence === 'none') return false;

      const evDate = new Date(ev.event_date + 'T00:00:00');
      if (dateObj < evDate) return false;
      if (ev.recurrence_end_date && dateObj > new Date(ev.recurrence_end_date + 'T00:00:00')) return false;

      const diffDays = Math.floor((dateObj - evDate) / (1000 * 60 * 60 * 24));
      if (ev.recurrence === 'weekly') return diffDays % 7 === 0;
      if (ev.recurrence === 'biweekly') return diffDays % 14 === 0;
      if (ev.recurrence === 'monthly') return evDate.getDate() === day;
      return false;
    }).filter(ev => !categoryFilter || ev.category === categoryFilter);
  };

  const handleDayClick = (day) => {
    setSelectedDay(selectedDay === day ? null : day);
  };

  const openAddEvent = () => {
    if (selectedDay) {
      setModalDate(new Date(year, month, selectedDay));
      setShowEventModal(true);
    }
  };

  const handleSaveEvent = async (eventData) => {
    if (eventData.id) {
      await supabase.from('events').update(eventData).eq('id', eventData.id);
    } else {
      await supabase.from('events').insert([eventData]);
    }
    fetchEvents();
  };

  const handleDeleteEvent = async (eventId) => {
    await supabase.from('events').delete().eq('id', eventId);
    fetchEvents();
  };

  const cells = [];
  for (let i = 0; i < firstDay; i++) {
    cells.push(<div key={`empty-${i}`} style={{ padding: '22px 8px 18px' }} />);
  }
  for (let day = 1; day <= daysInMonth; day++) {
    const dayEvents = getEventsForDay(day);
    const isToday = isCurrentMonth && today.getDate() === day;
    const isSelected = selectedDay === day;

    // Get unique category colors for dots
    const dotColors = [...new Set(dayEvents.map(e => CATEGORIES[e.category]?.color || '#ea580c'))];

    cells.push(
      <div
        key={day}
        onClick={() => handleDayClick(day)}
        style={{
          padding: '22px 8px 18px',
          textAlign: 'center',
          borderRadius: '10px',
          cursor: 'pointer',
          position: 'relative',
          backgroundColor: isSelected ? '#1a1a1a' : isToday ? theme.primaryBg : 'transparent',
          color: isSelected ? '#ffffff' : '#1a1a1a',
          fontWeight: isToday || dayEvents.length > 0 ? '700' : '400',
          fontSize: '0.9rem',
          transition: 'all 0.15s',
          border: isToday && !isSelected ? `2px solid ${theme.primary}` : '2px solid transparent'
        }}
      >
        {day}
        {dotColors.length > 0 && (
          <div style={{
            display: 'flex', justifyContent: 'center', gap: '3px', marginTop: '4px'
          }}>
            {dotColors.map((color, i) => (
              <div key={i} style={{
                width: '5px', height: '5px', borderRadius: '50%',
                backgroundColor: isSelected ? '#ffffff' : color
              }} />
            ))}
          </div>
        )}
      </div>
    );
  }

  const selectedDayEvents = selectedDay ? getEventsForDay(selectedDay) : [];
  const hasSelection = selectedDay !== null;

  // Use the shared module-level formatTime12h helper
  const formatTime = formatTime12h;

  return (
    <>
      <div style={{
        borderRadius: '16px',
        border: '1px solid #eee',
        overflow: 'hidden',
        backgroundColor: '#ffffff',
        transition: 'all 0.3s ease'
      }}>
        {/* Shared header bar */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '16px 20px', backgroundColor: theme.primary, color: '#ffffff'
        }}>
          <button onClick={prevMonth} style={{
            background: 'none', border: 'none', color: 'rgba(255,255,255,0.7)',
            fontSize: '0.8rem', cursor: 'pointer', fontWeight: '600', padding: '6px 10px'
          }}>Prev</button>
          <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '700' }}>
            {hasSelection
              ? `${monthName} ${selectedDay}, ${year}`
              : `${monthName} ${year}`
            }
          </h3>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            {hasSelection && isStaff && (
              <button onClick={openAddEvent} style={{
                background: 'rgba(255,255,255,0.2)', border: 'none', color: '#fff',
                borderRadius: '6px', padding: '4px 12px', cursor: 'pointer',
                fontSize: '1rem', fontWeight: '700', lineHeight: '1'
              }}>+</button>
            )}
            {hasSelection && (
              <button onClick={() => setSelectedDay(null)} style={{
                background: 'rgba(255,255,255,0.15)', border: 'none', color: 'rgba(255,255,255,0.8)',
                borderRadius: '6px', padding: '4px 10px', cursor: 'pointer',
                fontSize: '0.7rem', fontWeight: '600'
              }}>Close</button>
            )}
            <button onClick={nextMonth} style={{
              background: 'none', border: 'none', color: 'rgba(255,255,255,0.7)',
              fontSize: '0.8rem', cursor: 'pointer', fontWeight: '600', padding: '6px 10px'
            }}>Next</button>
          </div>
        </div>

        {/* Body */}
        <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row' }}>
        {/* Calendar side */}
        <div style={{
          flex: isMobile ? '1' : (hasSelection ? '0 0 58%' : '1'),
          transition: 'flex 0.3s ease',
          minWidth: 0
        }}>

          {/* Day headers */}
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)',
            padding: '10px 12px 4px', borderBottom: '1px solid #f0f0f0'
          }}>
            {dayNames.map(d => (
              <div key={d} style={{
                textAlign: 'center', fontSize: '0.7rem', fontWeight: '700',
                color: '#999', letterSpacing: '0.05em', padding: '4px'
              }}>{d}</div>
            ))}
          </div>

          {/* Day grid */}
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)',
            padding: '8px 12px 20px', gap: '4px'
          }}>
            {cells}
          </div>

          {/* Legend */}
          <div style={{
            display: 'flex', gap: '16px', padding: '12px 20px 16px',
            borderTop: '1px solid #f0f0f0', flexWrap: 'wrap'
          }}>
            {Object.entries(CATEGORIES).map(([key, { label, color }]) => (
              <div key={key} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <div style={{
                  width: '8px', height: '8px', borderRadius: '50%', backgroundColor: color
                }} />
                <span style={{ fontSize: '0.7rem', color: '#888', fontWeight: '600' }}>{label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Day detail panel (slides in from right, or below on mobile) */}
        {hasSelection && (
          <div ref={detailPanelRef} style={{
            flex: isMobile ? '1' : '0 0 42%',
            borderLeft: isMobile ? 'none' : '1px solid #eee',
            borderTop: isMobile ? '1px solid #eee' : 'none',
            backgroundColor: '#fafafa',
            padding: '0',
            display: 'flex',
            flexDirection: 'column',
            scrollMarginTop: '80px',
            animation: isMobile ? 'none' : 'slideIn 0.25s ease-out'
          }}>
            {/* Events list */}
            <div style={{ padding: '16px', flex: 1, overflowY: 'auto' }}>
              {selectedDayEvents.length > 0 ? (
                selectedDayEvents.map((event) => {
                  const catColor = CATEGORIES[event.category]?.color || '#ea580c';
                  return (
                    <div key={event.id} style={{
                      padding: '14px 16px',
                      backgroundColor: '#ffffff',
                      borderRadius: '10px',
                      border: '1px solid #eee',
                      marginBottom: '10px',
                      borderLeft: `4px solid ${catColor}`
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                        <span style={{
                          fontSize: '0.6rem', fontWeight: '700', color: catColor,
                          backgroundColor: catColor + '15', padding: '2px 8px',
                          borderRadius: '4px', textTransform: 'uppercase', letterSpacing: '0.05em'
                        }}>
                          {CATEGORIES[event.category]?.label || 'Other'}
                        </span>
                      </div>
                      <div style={{ fontWeight: '700', fontSize: '0.95rem', color: '#1a1a1a', marginBottom: '4px' }}>
                        {event.title}
                      </div>
                      <div style={{ fontSize: '0.8rem', color: '#888' }}>
                        {formatTime(event.start_time)} - {formatTime(event.end_time)}
                        {event.location && ` | ${event.location}`}
                      </div>
                      {event.description && (
                        <div className="event-md" style={{ fontSize: '0.8rem', color: '#666', marginTop: '6px', lineHeight: '1.4' }}>
                          <ReactMarkdown
                            components={{
                              p: ({ node, children, ...props }) => <p style={{ margin: '0 0 6px 0' }} {...props}>{children}</p>,
                              strong: ({ node, children, ...props }) => <strong style={{ color: '#1a1a1a' }} {...props}>{children}</strong>,
                              a: ({ node, children, href, ...props }) => <a style={{ color: theme.primary }} href={href} target="_blank" rel="noopener noreferrer" {...props}>{children}</a>
                            }}
                          >
                            {event.description}
                          </ReactMarkdown>
                        </div>
                      )}
                      {event.recurrence !== 'none' && (
                        <div style={{
                          fontSize: '0.7rem', color: '#999', marginTop: '6px',
                          fontStyle: 'italic'
                        }}>
                          Repeats {event.recurrence}
                        </div>
                      )}
                      {isStaff && (
                        <div style={{ display: 'flex', gap: '6px', marginTop: '10px' }}>
                          <button onClick={() => {
                            setModalDate(new Date(year, month, selectedDay));
                            setShowEventModal(true);
                          }} style={{
                            background: '#f0f0f0', border: 'none', borderRadius: '6px',
                            padding: '5px 12px', fontSize: '0.7rem', fontWeight: '600',
                            cursor: 'pointer', color: '#333'
                          }}>Edit</button>
                          <button onClick={() => handleDeleteEvent(event.id)} style={{
                            background: theme.primaryBg, border: 'none', borderRadius: '6px',
                            padding: '5px 12px', fontSize: '0.7rem', fontWeight: '600',
                            cursor: 'pointer', color: theme.primary
                          }}>Delete</button>
                        </div>
                      )}
                    </div>
                  );
                })
              ) : (
                <div style={{
                  textAlign: 'center', color: '#bbb', fontSize: '0.85rem',
                  padding: '40px 20px'
                }}>
                  No events this day
                </div>
              )}
            </div>
          </div>
        )}
      </div>{/* end body flex */}
      </div>{/* end outer container */}

      {/* Staff event modal */}
      {showEventModal && modalDate && (
        <EventModal
          date={modalDate}
          existingEvents={getEventsForDay(modalDate.getDate())}
          onClose={() => setShowEventModal(false)}
          onSave={handleSaveEvent}
          onDelete={handleDeleteEvent}
          isMobile={isMobile}
          staff={staff}
        />
      )}
    </>
  );
}

// ─── Visit Us Section ─────────────────────────────────────
function VisitUsSection({ isMobile }) {
  return (
    <div id="visit-us" style={{
      padding: isMobile ? '48px 20px' : '64px 48px',
      maxWidth: '1200px',
      margin: '0 auto'
    }}>
      <SectionHeader title="Come Visit" subtitle="Find us in the pines on Lake Drive" />
      <div style={{
        borderRadius: '16px',
        overflow: 'hidden',
        marginBottom: '24px',
        maxHeight: isMobile ? '240px' : '380px'
      }}>
        <img src="/ppc-photos/storefront-day-flowers.jpg" alt="Poke Pine Cabin storefront" style={{
          width: '100%', height: '100%', objectFit: 'cover', display: 'block', maxHeight: isMobile ? '240px' : '380px'
        }} />
      </div>
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
        gap: '24px'
      }}>
        {/* Location & Contact */}
        <div style={{
          backgroundColor: '#fff',
          borderRadius: '14px',
          border: '1px solid #eee',
          padding: '28px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
            <MapPin size={20} color={theme.primary} />
            <h3 style={{ fontSize: '1rem', fontWeight: '800', color: '#1a1a1a', margin: 0 }}>Location</h3>
          </div>
          <p style={{ fontSize: '0.9rem', color: '#444', margin: '0 0 4px 0', lineHeight: '1.6' }}>
            23872 Lake Dr Unit B-C
          </p>
          <p style={{ fontSize: '0.9rem', color: '#444', margin: '0 0 16px 0', lineHeight: '1.6' }}>
            Crestline, CA 92325
          </p>
          <p style={{ fontSize: '0.8rem', color: '#888', margin: '0 0 24px 0' }}>
            Up in the San Bernardino mountains
          </p>

          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
            <Phone size={20} color={theme.primary} />
            <h3 style={{ fontSize: '1rem', fontWeight: '800', color: '#1a1a1a', margin: 0 }}>Phone</h3>
          </div>
          <a
            href="tel:+19093219555"
            style={{
              display: 'inline-block',
              backgroundColor: theme.primary,
              color: '#fff',
              padding: '12px 28px',
              borderRadius: '10px',
              fontSize: '0.95rem',
              fontWeight: '700',
              textDecoration: 'none',
              transition: 'opacity 0.2s',
              marginBottom: '24px'
            }}
            onMouseEnter={e => e.currentTarget.style.opacity = '0.85'}
            onMouseLeave={e => e.currentTarget.style.opacity = '1'}
          >
            (909) 321-9555
          </a>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <a href="mailto:info@pokepinecabin.com" style={{
              display: 'flex', alignItems: 'center', gap: '8px',
              fontSize: '0.85rem', fontWeight: '600', color: theme.primary, textDecoration: 'none'
            }}>
              <Mail size={16} /> info@pokepinecabin.com
            </a>
            <a href="https://www.instagram.com/pokepinecabin/" target="_blank" rel="noopener noreferrer" style={{
              display: 'flex', alignItems: 'center', gap: '8px',
              fontSize: '0.85rem', fontWeight: '600', color: theme.primary, textDecoration: 'none'
            }}>
              <IgIcon size={16} /> @pokepinecabin
            </a>
            <a href="https://www.facebook.com/Pokepinecabin" target="_blank" rel="noopener noreferrer" style={{
              display: 'flex', alignItems: 'center', gap: '8px',
              fontSize: '0.85rem', fontWeight: '600', color: theme.primary, textDecoration: 'none'
            }}>
              <FbIcon size={16} /> Pokepinecabin
            </a>
          </div>
        </div>

        {/* Hours */}
        <div style={{
          backgroundColor: '#fff',
          borderRadius: '14px',
          border: '1px solid #eee',
          padding: '28px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
            <Clock size={20} color={theme.primary} />
            <h3 style={{ fontSize: '1rem', fontWeight: '800', color: '#1a1a1a', margin: 0 }}>Hours</h3>
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <tbody>
              {[
                ['Sunday', '11:30 AM - 6 PM'],
                ['Monday', 'Closed'],
                ['Tuesday', 'Closed'],
                ['Wednesday', 'Closed'],
                ['Thursday', '12:30 PM - 6 PM'],
                ['Friday', '12:30 PM - 7 PM'],
                ['Saturday', '11:30 AM - 7 PM']
              ].map(([day, hours]) => (
                <tr key={day}>
                  <td style={{
                    padding: '8px 0',
                    fontSize: '0.9rem',
                    fontWeight: '600',
                    color: hours === 'Closed' ? '#999' : '#1a1a1a',
                    borderBottom: '1px solid #f0f0f0'
                  }}>{day}</td>
                  <td style={{
                    padding: '8px 0',
                    fontSize: '0.9rem',
                    color: hours === 'Closed' ? '#ccc' : '#444',
                    textAlign: 'right',
                    fontWeight: hours === 'Closed' ? '400' : '600',
                    borderBottom: '1px solid #f0f0f0'
                  }}>{hours}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ─── Footer ───────────────────────────────────────────────
function Footer() {
  return (
    <footer style={{
      backgroundColor: '#1a1a1a',
      color: '#999',
      padding: '40px 24px',
      textAlign: 'center'
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        margin: '0 auto 16px'
      }}>
        <img src="/full-logo.jpg" alt="Poke Pine Cabin" style={{ height: '72px', width: 'auto', objectFit: 'contain', borderRadius: '12px' }} />
      </div>
      <p style={{ fontSize: '0.9rem', fontWeight: '600', color: '#ccc', margin: '0 0 8px 0' }}>
        Poke Pine <span style={{ color: theme.primary }}>Cabin</span>
      </p>
      <p style={{ fontSize: '0.75rem', margin: '0 0 12px 0' }}>
        Family-owned Pokemon shop in the Crestline mountains
      </p>
      <div style={{ display: 'flex', justifyContent: 'center', gap: '16px', flexWrap: 'wrap', marginBottom: '12px' }}>
        <span style={{ color: '#ccc', fontSize: '0.75rem', fontWeight: '600' }}>23872 Lake Dr Unit B-C, Crestline, CA 92325</span>
      </div>
      <div style={{ display: 'flex', justifyContent: 'center', gap: '16px', flexWrap: 'wrap', marginBottom: '12px' }}>
        <a href="tel:+19093219555" style={{ color: '#ccc', textDecoration: 'none', fontSize: '0.75rem', fontWeight: '600' }}>(909) 321-9555</a>
        <a href="mailto:info@pokepinecabin.com" style={{ color: '#ccc', textDecoration: 'none', fontSize: '0.75rem', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '4px' }}><Mail size={13} /> info@pokepinecabin.com</a>
      </div>
      <div style={{ display: 'flex', justifyContent: 'center', gap: '16px', flexWrap: 'wrap', marginBottom: '20px' }}>
        <a href="https://www.instagram.com/pokepinecabin/" target="_blank" rel="noopener noreferrer" style={{ color: '#ccc', textDecoration: 'none', fontSize: '0.75rem', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '4px' }}><IgIcon size={13} /> @pokepinecabin</a>
        <a href="https://www.facebook.com/Pokepinecabin" target="_blank" rel="noopener noreferrer" style={{ color: '#ccc', textDecoration: 'none', fontSize: '0.75rem', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '4px' }}><FbIcon size={13} /> Pokepinecabin</a>
      </div>
      <a
        href="https://appcatalyst.org"
        target="_blank"
        rel="noopener noreferrer"
        style={{
          color: '#555',
          textDecoration: 'none',
          fontSize: '0.65rem',
          transition: 'color 0.2s'
        }}
      >
        Built by App Catalyst
      </a>
    </footer>
  );
}

// ─── Page Wrapper ─────────────────────────────────────────
function PageWrapper({ children, isMobile }) {
  return (
    <>
      <main style={{ maxWidth: '1100px', margin: '0 auto', padding: isMobile ? '100px 16px 40px' : '120px 24px 60px' }}>
        {children}
      </main>
      <VisitUsSection isMobile={isMobile} />
      <Footer />
    </>
  );
}

// ─── Home Page ────────────────────────────────────────────
function OpenNowBanner({ isMobile }) {
  const STORE_HOURS = {
    0: [11.5, 18], // Sunday 11:30 AM - 6 PM
    1: null,       // Monday closed
    2: null,       // Tuesday closed
    3: null,       // Wednesday closed
    4: [12.5, 18], // Thursday 12:30 PM - 6 PM
    5: [12.5, 19], // Friday 12:30 PM - 7 PM
    6: [11.5, 19], // Saturday 11:30 AM - 7 PM
  };

  const DAY_THEMES = {};

  const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  const now = new Date();
  const dow = now.getDay();
  const hours = STORE_HOURS[dow];
  const currentHour = now.getHours() + now.getMinutes() / 60;
  const isOpen = hours && currentHour >= hours[0] && currentHour < hours[1];

  // Find next open day with an event
  let nextDayName = '';
  let nextTheme = '';
  if (!isOpen) {
    for (let offset = 1; offset <= 7; offset++) {
      const checkDow = (dow + offset) % 7;
      if (STORE_HOURS[checkDow] && DAY_THEMES[checkDow]) {
        nextDayName = DAY_NAMES[checkDow];
        nextTheme = DAY_THEMES[checkDow];
        break;
      }
    }
  }

  if (isOpen) {
    return (
      <Link to="/calendar" style={{ textDecoration: 'none' }}>
        <div style={{
          backgroundColor: '#f0fdf4',
          border: '1px solid #bbf7d0',
          borderRadius: '12px',
          padding: '14px 20px',
          margin: isMobile ? '0 16px 24px' : '0 auto 32px',
          maxWidth: '1100px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'pointer',
          transition: 'all 0.15s',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{
              width: '10px', height: '10px', borderRadius: '50%', backgroundColor: '#22c55e',
              boxShadow: '0 0 6px rgba(34,197,94,0.5)',
            }} />
            <span style={{ fontSize: '0.9rem', fontWeight: '700', color: '#166534' }}>
              We're open right now
            </span>
            <span style={{ fontSize: '0.8rem', color: '#15803d' }}>
              See what's happening today
            </span>
          </div>
          <span style={{ fontSize: '1.1rem', color: '#22c55e', fontWeight: '700' }}>&#8250;</span>
        </div>
      </Link>
    );
  }

  // Closed -- show next event
  return (
    <Link to="/calendar" style={{ textDecoration: 'none' }}>
      <div style={{
        background: 'linear-gradient(135deg, #1a1a1a, #2d2d2d)',
        borderRadius: '16px',
        padding: isMobile ? '20px' : '24px 32px',
        margin: isMobile ? '24px 16px 32px' : '40px auto 48px',
        maxWidth: '1100px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        cursor: 'pointer',
        transition: 'all 0.2s',
        boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
      }}>
        <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'flex-start' : 'center', gap: isMobile ? '6px' : '14px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{
              width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#ef4444',
            }} />
            <span style={{ fontSize: '0.8rem', fontWeight: '700', color: '#999', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Currently closed
            </span>
          </div>
          {nextTheme && (
            <span style={{ fontSize: isMobile ? '1rem' : '1.1rem', fontWeight: '700', color: '#fff' }}>
              Up next: <span style={{ color: theme.primary }}>{nextTheme}</span> on {nextDayName}
            </span>
          )}
        </div>
        <div style={{
          backgroundColor: theme.primary,
          borderRadius: '8px',
          padding: isMobile ? '6px 12px' : '8px 16px',
          color: '#fff',
          fontSize: '0.75rem',
          fontWeight: '700',
          flexShrink: 0,
        }}>
          See Calendar
        </div>
      </div>
    </Link>
  );
}

function HomePage({ isMobile }) {
  return (
    <>
      {/* Hero - Full Viewport */}
      <header style={{
        height: '100vh',
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        {/* Background image */}
        <div style={{
          position: 'absolute',
          inset: 0,
          backgroundImage: 'url(/top-background.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          filter: 'brightness(0.8)'
        }} />
        {/* Overlay */}
        <div style={{
          position: 'absolute',
          inset: 0,
          background: 'linear-gradient(to bottom, rgba(0,0,0,0.2), rgba(0,0,0,0.55))'
        }} />
        {/* Content */}
        <div style={{
          position: 'relative',
          zIndex: 2,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          padding: '24px'
        }}>
          <div style={{
            width: isMobile ? '170px' : '230px',
            height: isMobile ? '170px' : '230px',
            borderRadius: '50%',
            backgroundColor: '#ffffff',
            boxShadow: '0 8px 40px rgba(0,0,0,0.35)',
            marginBottom: '24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden'
          }}>
            <img src="/pokepineballonly.jpg" alt="Poke Pine Cabin" style={{
              width: '92%', height: '92%', objectFit: 'contain'
            }} />
          </div>
          <h1 style={{
            fontSize: isMobile ? 'clamp(2.2rem, 10vw, 3.5rem)' : '5rem',
            fontWeight: '900',
            margin: '0 0 12px 0',
            letterSpacing: '-0.03em',
            color: '#ffffff',
            textShadow: '0 2px 20px rgba(0,0,0,0.4)',
            textAlign: 'center'
          }}>
            Poke Pine <span style={{ color: theme.primary, backgroundColor: '#ffffff', padding: '0px 5px', borderRadius: '6px', marginLeft: '4px' }}>Cabin</span>
          </h1>
          <p style={{
            fontSize: isMobile ? 'clamp(1rem, 4vw, 1.3rem)' : '1.6rem',
            color: 'rgba(255,255,255,0.9)',
            maxWidth: '600px',
            margin: '0 auto 40px',
            textAlign: 'center',
            textShadow: '0 1px 8px rgba(0,0,0,0.3)'
          }}>
            Crestline's family-owned Pokemon-only shop.
          </p>
          {/* Marquee Banner */}
          <div style={{
            width: '700px',
            maxWidth: '90vw',
            overflow: 'hidden',
            marginTop: '36px',
            position: 'relative',
            maskImage: 'linear-gradient(to right, transparent, black 15%, black 85%, transparent)',
            WebkitMaskImage: 'linear-gradient(to right, transparent, black 15%, black 85%, transparent)'
          }}>
            <div style={{
              display: 'flex',
              animation: 'marquee 12s linear infinite',
              whiteSpace: 'nowrap',
              width: 'max-content'
            }}>
              {[...Array(4)].map((_, i) => (
                <span key={i} style={{
                  fontSize: isMobile ? '1rem' : '1.3rem',
                  fontWeight: '800',
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase'
                }}>
                  <span style={{ color: '#ffffff', margin: isMobile ? '0 12px' : '0 20px' }}>Buy</span>
                  <span style={{ color: theme.primary, margin: '0 8px' }}>-</span>
                  <span style={{ color: '#ffffff', margin: isMobile ? '0 12px' : '0 20px' }}>Battle</span>
                  <span style={{ color: theme.primary, margin: '0 8px' }}>-</span>
                  <span style={{ color: '#ffffff', margin: isMobile ? '0 12px' : '0 20px' }}>Collect</span>
                  <span style={{ color: theme.primary, margin: '0 8px' }}>-</span>
                  <span style={{ color: '#ffffff', margin: isMobile ? '0 12px' : '0 20px' }}>Sell</span>
                  <span style={{ color: theme.primary, margin: '0 8px' }}>-</span>
                  <span style={{ color: '#ffffff', margin: isMobile ? '0 12px' : '0 20px' }}>Donate</span>
                  <span style={{ color: theme.primary, margin: '0 8px' }}>-</span>
                </span>
              ))}
            </div>
          </div>
        </div>
      </header>

      <OpenNowBanner isMobile={isMobile} />

      {/* Homeschool 3D Printing Promotional Banner */}
      <Link to="/homeschool-3d-printing" style={{ textDecoration: 'none' }}>
        <div style={{
          border: '2px solid #C8102E',
          borderRadius: '16px',
          padding: isMobile ? '18px 20px' : '22px 32px',
          margin: isMobile ? '0 16px 16px' : '0 auto 20px',
          maxWidth: '1100px',
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: isMobile ? '14px' : '24px',
          cursor: 'pointer',
          transition: 'transform 0.2s, box-shadow 0.2s',
          boxShadow: '0 6px 24px rgba(200,16,46,0.15)',
          backgroundColor: '#fff',
        }}
        onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 10px 32px rgba(200,16,46,0.25)'; }}
        onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 6px 24px rgba(200,16,46,0.15)'; }}
        >
          <div style={{ textAlign: isMobile ? 'center' : 'left', flex: 1 }}>
            <p style={{ fontSize: '0.7rem', fontWeight: '800', color: theme.primary, letterSpacing: '2px', textTransform: 'uppercase', margin: '0 0 4px 0', display: 'flex', alignItems: 'center', gap: '6px', justifyContent: isMobile ? 'center' : 'flex-start' }}>
              <Printer size={14} /> Now Enrolling
            </p>
            <p style={{ fontSize: isMobile ? '1.1rem' : '1.35rem', fontWeight: '900', color: '#1a1a1a', margin: '0 0 4px 0', letterSpacing: '-0.01em' }}>
              Homeschool 3D Printing School
            </p>
            <p style={{ fontSize: '0.9rem', color: '#555', margin: 0 }}>
              Daily STEM program · Ages 8–16 · Crestline, CA
            </p>
          </div>
          <div style={{
            backgroundColor: theme.primary,
            color: '#fff',
            padding: '12px 24px',
            borderRadius: '8px',
            fontSize: '0.9rem',
            fontWeight: '800',
            letterSpacing: '0.5px',
            whiteSpace: 'nowrap',
            flexShrink: 0,
          }}>
            Learn More →
          </div>
        </div>
      </Link>

      {/* Summer Camp Promotional Banner — hides after camp ends (Aug 8, 2026) */}
      {!isPostCamp() && (
      <Link to="/summer-camp" style={{ textDecoration: 'none' }}>
        <div className="summer-camp-banner" style={{
          border: '2px solid #C8102E',
          borderRadius: '16px',
          padding: isMobile ? '18px 20px' : '22px 32px',
          margin: isMobile ? '0 16px 24px' : '0 auto 32px',
          maxWidth: '1100px',
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: isMobile ? '14px' : '24px',
          cursor: 'pointer',
          transition: 'transform 0.2s, box-shadow 0.2s',
          boxShadow: '0 6px 24px rgba(200,16,46,0.15)',
        }}
        onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 10px 32px rgba(200,16,46,0.25)'; }}
        onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 6px 24px rgba(200,16,46,0.15)'; }}
        >
          <div style={{ textAlign: isMobile ? 'center' : 'left', flex: 1 }}>
            <p style={{ fontSize: '0.7rem', fontWeight: '800', color: theme.primary, letterSpacing: '2px', textTransform: 'uppercase', margin: '0 0 4px 0', display: 'flex', alignItems: 'center', gap: '6px', justifyContent: isMobile ? 'center' : 'flex-start' }}>
              <Sun size={14} /> New This Summer
            </p>
            <p style={{ fontSize: isMobile ? '1.1rem' : '1.35rem', fontWeight: '900', color: '#1a1a1a', margin: '0 0 4px 0', letterSpacing: '-0.01em' }}>
              Summer Adventure Journal Club
            </p>
            <p style={{ fontSize: '0.9rem', color: '#555', margin: 0 }}>
              Creative outdoor fun for kids · June 1 – August 7
            </p>
          </div>
          <div style={{
            backgroundColor: theme.primary,
            color: '#fff',
            padding: '12px 24px',
            borderRadius: '8px',
            fontSize: '0.9rem',
            fontWeight: '800',
            letterSpacing: '0.5px',
            whiteSpace: 'nowrap',
            flexShrink: 0,
          }}>
            Learn More →
          </div>
        </div>
      </Link>
      )}

      {/* Mission section + Visit Us + Footer */}
      <main style={{ maxWidth: '1100px', margin: '0 auto', padding: isMobile ? '40px 16px' : '60px 24px' }}>
        <div id="mission" style={{ marginBottom: '96px' }}>
          <SectionHeader title="Join Our Family" subtitle="This isn't just a shop. It's where the mountain comes to talk Pokemon." />
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
            gap: '24px',
            alignItems: 'stretch',
            maxWidth: '1000px',
            margin: '0 auto'
          }}>
            <div style={{
              backgroundColor: '#ffffff',
              borderRadius: '16px',
              border: '1px solid #eee',
              padding: isMobile ? '20px 16px' : '32px'
            }}>
              <p style={{
                fontSize: '1.05rem',
                color: '#333',
                lineHeight: '1.8',
                marginBottom: '18px'
              }}>
                Poke Pine Cabin is a <strong>family-owned Pokemon-only shop</strong> tucked up in the Crestline mountains. No distractions, no side hobbies. Just Pokemon. Cards, collectibles, sealed product, and a mountain-town atmosphere where kids, parents, and longtime collectors all feel at home.
              </p>
              <p style={{
                fontSize: '1.05rem',
                color: '#333',
                lineHeight: '1.8',
                marginBottom: '18px'
              }}>
                Stop in after a hike, bring the kids up for the weekend, or make the drive up the mountain for a full day of cards and conversation. We love talking Pokemon, helping new collectors get started, and making sure the mountain has a real home for the hobby.
              </p>
              <p style={{
                fontSize: '1.1rem',
                color: theme.primary,
                fontWeight: '700',
                fontStyle: 'italic',
                margin: 0,
                paddingTop: '6px'
              }}>
                Come find us in the pines. Pull up a chair and stay a while.
              </p>
            </div>
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gridTemplateRows: '1fr 1fr',
              gap: '12px',
              minHeight: isMobile ? '320px' : '100%'
            }}>
              <img src="/ppc-photos/family-couple-plushies.jpg" alt="Family at Poke Pine Cabin" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '12px', display: 'block' }} />
              <img src="/ppc-photos/kid-booster-packs.jpg" alt="Young trainer with booster packs" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '12px', display: 'block' }} />
              <img src="/ppc-photos/couple-welcome-chalkboard.jpg" alt="Customers at the shop" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '12px', display: 'block' }} />
              <img src="/ppc-photos/couple-funkopops.jpg" alt="Couple at Funko wall" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '12px', display: 'block' }} />
            </div>
          </div>
        </div>

        {/* ── COMMUNITY / TRADE NIGHTS ── */}
        <div id="community" style={{ marginBottom: '64px' }}>
          <SectionHeader title="Trade Nights & Community" subtitle="Gather round the table. Trade cards. Make friends." />
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
            gap: '16px',
            maxWidth: '1100px',
            margin: '0 auto'
          }}>
            {[
              { src: '/ppc-photos/trade-night-tables.jpg', title: 'Trade Nights', desc: 'Bring your binders. Pull up a chair. Trade with neighbors and folks you\'ll see again next week.' },
              { src: '/ppc-photos/game-room-kids-playing.jpg', title: 'Game Room', desc: 'Space to actually play TCG, hang out, or just watch the kids battle it out over a new pull.' },
              { src: '/ppc-photos/interior-trade-night-poster.jpg', title: 'Events & Tournaments', desc: 'Check the calendar for community days, special releases, and friendly tournaments up the mountain.' },
            ].map((item, i) => (
              <div key={i} style={{
                borderRadius: '16px', overflow: 'hidden', backgroundColor: '#ffffff',
                border: '1px solid #eee'
              }}>
                <img src={item.src} alt={item.title} style={{ width: '100%', height: '240px', objectFit: 'cover', display: 'block' }} />
                <div style={{ padding: '18px' }}>
                  <h3 style={{ fontSize: '1.15rem', fontWeight: '800', color: '#1a1a1a', margin: '0 0 6px 0' }}>{item.title}</h3>
                  <p style={{ fontSize: '0.85rem', color: '#666', lineHeight: '1.6', margin: 0 }}>{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ── CARDS ── */}
        <div id="cards" style={{ marginBottom: '64px' }}>
          <SectionHeader title="Cards" subtitle="We carry Pokemon cards from every era" />
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
            gap: '20px'
          }}>
            <div style={{
              borderRadius: '16px', overflow: 'hidden', backgroundColor: '#ffffff',
              border: '1px solid #eee', transition: 'transform 0.2s, box-shadow 0.2s', cursor: 'pointer'
            }}
            onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = '0 12px 32px rgba(0,0,0,0.1)'; }}
            onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
            >
              <img src="/ppc-photos/browsing-cards-case.jpg" alt="Vintage Cards" style={{ width: '100%', height: '360px', objectFit: 'cover', display: 'block' }} />
              <div style={{ padding: '20px' }}>
                <h3 style={{ fontSize: '1.3rem', fontWeight: '800', color: '#1a1a1a', margin: '0 0 4px 0' }}>Vintage & Classic</h3>
                <span style={{ fontSize: '0.8rem', fontWeight: '700', color: theme.primary, backgroundColor: theme.primaryBg, padding: '3px 10px', borderRadius: '6px' }}>1995 - 2017</span>
                <p style={{ fontSize: '0.85rem', color: '#666', lineHeight: '1.6', marginTop: '10px' }}>Base Set, Jungle, Fossil, Team Rocket, Neo series, Diamond & Pearl through Sun & Moon.</p>
              </div>
            </div>
            <div style={{
              borderRadius: '16px', overflow: 'hidden', backgroundColor: '#ffffff',
              border: '1px solid #eee', transition: 'transform 0.2s, box-shadow 0.2s', cursor: 'pointer'
            }}
            onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = '0 12px 32px rgba(0,0,0,0.1)'; }}
            onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
            >
              <img src="/ppc-photos/customer-card-display.jpg" alt="Modern Cards" style={{ width: '100%', height: '360px', objectFit: 'cover', display: 'block' }} />
              <div style={{ padding: '20px' }}>
                <h3 style={{ fontSize: '1.3rem', fontWeight: '800', color: '#1a1a1a', margin: '0 0 4px 0' }}>Current & Modern</h3>
                <span style={{ fontSize: '0.8rem', fontWeight: '700', color: theme.primary, backgroundColor: theme.primaryBg, padding: '3px 10px', borderRadius: '6px' }}>2018 - Present</span>
                <p style={{ fontSize: '0.85rem', color: '#666', lineHeight: '1.6', marginTop: '10px' }}>Sword & Shield, Scarlet & Violet. VMAX, VSTAR, ex cards, illustration rares, and sealed product.</p>
              </div>
            </div>
            <div style={{
              borderRadius: '16px', overflow: 'hidden', backgroundColor: '#ffffff',
              border: '1px solid #eee', transition: 'transform 0.2s, box-shadow 0.2s', cursor: 'pointer'
            }}
            onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = '0 12px 32px rgba(0,0,0,0.1)'; }}
            onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
            >
              <img src="/ppc-photos/slabbed-cards-case.jpg" alt="Graded Cards" style={{ width: '100%', height: '360px', objectFit: 'cover', display: 'block' }} />
              <div style={{ padding: '20px' }}>
                <h3 style={{ fontSize: '1.3rem', fontWeight: '800', color: '#1a1a1a', margin: '0 0 4px 0' }}>Graded Cards & Slabs</h3>
                <p style={{ fontSize: '0.85rem', color: '#666', lineHeight: '1.6', marginTop: '10px' }}>PSA, CGC, and BGS graded cards. Authenticated, encapsulated, and ready for your collection.</p>
              </div>
            </div>
          </div>
        </div>

        {/* ── MERCHANDISE & COLLECTIBLES ── */}
        <div id="merchandise" style={{ marginBottom: '64px' }}>
          <SectionHeader title="Merchandise & Collectibles" subtitle="Beyond the cards - consoles, plushies, figures, and more" />
          <PhotoGrid isMobile={isMobile} photos={[
            { src: '/ppc-photos/happy-place-plushies.jpg', alt: 'Plushies and merch' },
            { src: '/ppc-photos/cozy-corner-plushies.jpg', alt: 'Cozy corner with Snorlax and Piplup' },
            { src: '/ppc-photos/couch-good-vibes.jpg', alt: 'Good vibes seating area' },
          ]} />
        </div>
      </main>

      <VisitUsSection isMobile={isMobile} />
      <Footer />
    </>
  );
}

// ─── Consultation Page ────────────────────────────────────
function ConsultationPage({ isMobile }) {
  return (
    <PageWrapper isMobile={isMobile}>
      <div style={{ marginBottom: '64px' }}>
        <SectionHeader title="Consultation" subtitle="Learn before you sell, trade, or grade" />
        <div style={{
          backgroundColor: '#ffffff',
          borderRadius: '16px',
          border: '1px solid #eee',
          padding: isMobile ? '24px 16px' : '40px',
          maxWidth: '900px',
          margin: '0 auto'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
            <div style={{
              width: '48px', height: '48px', borderRadius: '12px',
              backgroundColor: theme.primaryBg, display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}>
              <GraduationCap size={24} color={theme.primary} />
            </div>
            <h3 style={{ fontSize: '1.3rem', fontWeight: '800', color: '#1a1a1a', margin: 0 }}>
              Private Consultation
            </h3>
          </div>
          <div style={{
            backgroundColor: theme.primaryBg,
            borderRadius: '10px',
            padding: '16px 20px',
            marginBottom: '24px',
            border: `1px solid ${theme.primaryBorder}`
          }}>
            <p style={{ fontSize: '0.95rem', color: theme.primary, fontWeight: '700', margin: '0 0 4px 0' }}>
              By Appointment
            </p>
            <p style={{ fontSize: '0.85rem', color: '#666', margin: 0 }}>
              Reach out to set up a private one-on-one consultation. We'll find a time that works around store hours.
            </p>
          </div>
          <p style={{ fontSize: '1rem', color: '#333', lineHeight: '1.8', marginBottom: '20px' }}>
            Whether you found a box of old cards in the attic or you have been collecting for years, we will sit down with you and walk through what you have. This is not a sales pitch. The goal is to educate you so you know what your collection is actually worth and you do not get taken advantage of.
          </p>
          <p style={{ fontSize: '1rem', color: '#333', lineHeight: '1.8', marginBottom: '28px' }}>
            We may make you an offer, but the real value of the consultation is the knowledge you walk away with.
          </p>

          <h4 style={{ fontSize: '1rem', fontWeight: '800', color: '#1a1a1a', margin: '0 0 16px 0' }}>What we cover:</h4>
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
            gap: '12px',
            marginBottom: '28px'
          }}>
            {[
              { title: 'Card Identification', desc: 'We help you identify what you have, from Base Set shadowless to modern illustration rares.' },
              { title: 'Market Value', desc: 'Learn what apps and tools to use to look up real-time prices so you always know what your cards are worth.' },
              { title: 'Grading Advice', desc: 'Not every card is worth grading. We show you how to evaluate condition and which cards are worth the investment.' },
              { title: 'Best Time to Buy or Sell', desc: 'Pokemon card values fluctuate with sets, seasons, and trends. We help you understand timing.' },
              { title: 'Pokemon History', desc: 'Understand the eras, the rare prints, the errors, and the cards that collectors chase. Knowledge is your best tool.' },
              { title: 'Vintage Collections', desc: 'Got old cards, sealed product, or Japanese imports? We help you sort through it all and understand what stands out.' }
            ].map((item, i) => (
              <div key={i} style={{
                padding: '16px',
                borderRadius: '10px',
                backgroundColor: '#fafafa',
                border: '1px solid #f0f0f0'
              }}>
                <h5 style={{ fontSize: '0.9rem', fontWeight: '700', color: theme.primary, margin: '0 0 4px 0' }}>{item.title}</h5>
                <p style={{ fontSize: '0.8rem', color: '#666', margin: 0, lineHeight: '1.5' }}>{item.desc}</p>
              </div>
            ))}
          </div>

          <div style={{
            backgroundColor: theme.primaryBg,
            borderRadius: '10px',
            padding: '20px',
          }}>
            <p style={{ fontSize: '0.95rem', color: theme.primary, fontWeight: '700', margin: '0 0 12px 0', textAlign: 'center' }}>
              Contact us to schedule
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'center' }}>
              <a href="tel:+19093219555" style={{
                fontSize: '1rem', fontWeight: '700', color: theme.primary, textDecoration: 'none'
              }}>
                (909) 321-9555
              </a>
              <a href="mailto:info@pokepinecabin.com" style={{
                fontSize: '0.9rem', fontWeight: '600', color: theme.primary, textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '6px', justifyContent: 'center'
              }}>
                <Mail size={15} /> info@pokepinecabin.com
              </a>
            </div>
          </div>
        </div>
      </div>
    </PageWrapper>
  );
}

// ─── Grading Page ─────────────────────────────────────────
function GradingPage({ isMobile }) {
  const steps = [
    { num: '1', title: 'Know What To Grade', desc: 'Not every card is worth the cost of grading. We help you spot the ones with real potential.' },
    { num: '2', title: 'Prep The Right Way', desc: 'Clean handling, no alterations, proper protection. We show you how to get cards ready without damaging them.' },
    { num: '3', title: 'Ship It Safely', desc: 'Photos, tracking, insurance, packaging. We walk you through how to send your cards to any grader with confidence.' },
  ];

  const graders = [
    {
      name: 'PSA',
      full: 'Professional Sports Authenticator',
      vibe: 'The gold standard. Highest resale value, especially on vintage.',
      note: 'Long turnarounds in 2026 (95+ days on value tiers), but PSA 10s still command the top dollar.'
    },
    {
      name: 'CGC',
      full: 'CGC Cards',
      vibe: 'Modern, consistent, growing fast. Uses half-point grades (9.5) for more nuance.',
      note: 'Faster turnarounds and competitive pricing. Well-respected for modern and Japanese Pokemon.'
    },
    {
      name: 'BGS',
      full: 'Beckett Grading Services',
      vibe: 'Best-in-class for sub-grades and presentation.',
      note: 'Black Label 10 (perfect across all four sub-grades) is the most prestigious grade in the hobby.'
    },
    {
      name: 'SGC',
      full: 'Sportscard Guaranty',
      vibe: 'Fastest turnarounds and distinctive slim slab design.',
      note: 'Smaller Pokemon presence than the big three, but loved for vintage and quick jobs.'
    },
  ];

  return (
    <PageWrapper isMobile={isMobile}>
      <div style={{ marginBottom: '64px' }}>
        <SectionHeader title="Grading" subtitle="We help you grade smart, not just grade" />

        {/* Slabs photo */}
        <div style={{
          borderRadius: '16px',
          overflow: 'hidden',
          maxWidth: '900px',
          margin: '0 auto 24px',
          maxHeight: isMobile ? '220px' : '300px'
        }}>
          <img src="/ppc-photos/slabbed-cards-case.jpg" alt="Graded slabs at Poke Pine Cabin" style={{
            width: '100%', height: '100%', objectFit: 'cover', display: 'block', maxHeight: isMobile ? '220px' : '300px'
          }} />
        </div>

        {/* Hero: Education promise */}
        <div style={{
          backgroundColor: '#1a1a1a',
          borderRadius: '16px',
          padding: isMobile ? '32px 20px' : '48px 40px',
          textAlign: 'center',
          marginBottom: '32px',
          maxWidth: '900px',
          margin: '0 auto 32px'
        }}>
          <p style={{ fontSize: isMobile ? '0.9rem' : '1.1rem', color: '#999', fontWeight: '600', margin: '0 0 8px 0' }}>
            Want to learn how to grade your cards?
          </p>
          <h2 style={{ fontSize: isMobile ? '2.2rem' : '3rem', fontWeight: '900', color: '#ffffff', margin: '0 0 16px 0', letterSpacing: '-0.03em', lineHeight: '1.1' }}>
            We'll teach you the whole process.
          </h2>
          <p style={{ fontSize: isMobile ? '0.9rem' : '1.05rem', color: '#ccc', margin: 0, maxWidth: '600px', marginLeft: 'auto', marginRight: 'auto', lineHeight: '1.6' }}>
            Which cards are actually worth grading, how to keep them in pristine condition, and how to safely send them to a grader. Come in with questions. Leave with a plan.
          </p>
        </div>

        {/* 3 Steps with arrows */}
        <div style={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: 'center',
          justifyContent: 'center',
          gap: isMobile ? '0' : '0',
          marginBottom: '40px',
          maxWidth: '900px',
          margin: '0 auto 40px'
        }}>
          {steps.map((step, i) => (
            <React.Fragment key={i}>
              <div style={{
                flex: 1,
                padding: isMobile ? '24px 20px' : '32px 24px',
                borderRadius: '14px',
                backgroundColor: '#ffffff',
                border: '2px solid #eee',
                textAlign: 'center',
                minWidth: 0,
              }}>
                <div style={{
                  width: '48px', height: '48px', borderRadius: '50%',
                  backgroundColor: theme.primary, color: '#fff',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '1.3rem', fontWeight: '900',
                  margin: '0 auto 14px',
                }}>
                  {step.num}
                </div>
                <h3 style={{ fontSize: '1.1rem', fontWeight: '800', color: '#1a1a1a', margin: '0 0 8px 0' }}>
                  {step.title}
                </h3>
                <p style={{ fontSize: '0.85rem', color: '#666', margin: 0, lineHeight: '1.5' }}>
                  {step.desc}
                </p>
              </div>
              {i < 2 && (
                <div style={{
                  padding: isMobile ? '8px 0' : '0 8px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: theme.primary, fontSize: '1.8rem', fontWeight: '900', flexShrink: 0,
                }}>
                  {isMobile ? (
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={theme.primary} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 5v14" /><path d="m19 12-7 7-7-7" />
                    </svg>
                  ) : (
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={theme.primary} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M5 12h14" /><path d="m12 5 7 7-7 7" />
                    </svg>
                  )}
                </div>
              )}
            </React.Fragment>
          ))}
        </div>

        {/* What we help with */}
        <div style={{
          backgroundColor: '#ffffff',
          borderRadius: '16px',
          border: '1px solid #eee',
          padding: isMobile ? '24px 16px' : '40px',
          maxWidth: '900px',
          margin: '0 auto 32px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
            <div style={{
              width: '48px', height: '48px', borderRadius: '12px',
              backgroundColor: theme.primaryBg, display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}>
              <Award size={24} color={theme.primary} />
            </div>
            <h3 style={{ fontSize: '1.3rem', fontWeight: '800', color: '#1a1a1a', margin: 0 }}>
              What we help you with
            </h3>
          </div>
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
            gap: '12px',
          }}>
            {[
              { title: 'Which cards to grade', desc: 'Honestly, it depends. Rarity, demand, and condition all play a role. We sit down with you and help you think it through so the math makes sense before you ever pay a grading fee.' },
              { title: 'Reading condition like a grader', desc: 'Centering, corners, edges, surface. We walk you through what graders tend to focus on so you can make a more informed guess at where a card might land.' },
              { title: 'Handling with care', desc: 'There is a line between careful handling and altering a card. We help you understand where that line sits so you can protect what you have without accidentally doing more harm than good.' },
              { title: 'Prep and protection', desc: 'Penny sleeves, semi-rigid holders, Card Savers. We talk through what tends to work best, a few common pitfalls, and how to pick what fits your situation.' },
              { title: 'Photos before you ship', desc: 'We generally recommend documenting both sides of each card in good lighting before sending. It takes a few minutes and gives you peace of mind if anything ever comes up.' },
              { title: 'Tracking and insurance', desc: 'Signature confirmation, declared-value insurance, and saving your tracking numbers. Small steps that make a big difference if a package ever takes a detour.' },
            ].map((item, i) => (
              <div key={i} style={{
                padding: '16px',
                borderRadius: '10px',
                backgroundColor: '#fafafa',
                border: '1px solid #f0f0f0'
              }}>
                <h5 style={{ fontSize: '0.9rem', fontWeight: '700', color: theme.primary, margin: '0 0 4px 0' }}>{item.title}</h5>
                <p style={{ fontSize: '0.8rem', color: '#666', margin: 0, lineHeight: '1.5' }}>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Where to send */}
        <div style={{
          backgroundColor: '#ffffff',
          borderRadius: '16px',
          border: '1px solid #eee',
          padding: isMobile ? '24px 16px' : '40px',
          maxWidth: '900px',
          margin: '0 auto 32px'
        }}>
          <h3 style={{ fontSize: '1.3rem', fontWeight: '800', color: '#1a1a1a', margin: '0 0 6px 0' }}>
            Where we'll send you
          </h3>
          <p style={{ fontSize: '0.9rem', color: '#666', lineHeight: '1.6', marginBottom: '20px' }}>
            We're grader-agnostic. We'll help you pick the right company for your cards based on turnaround, cost, and what collectors pay more for. Here are the four we trust most:
          </p>
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
            gap: '14px',
          }}>
            {graders.map((g, i) => (
              <div key={i} style={{
                padding: '18px',
                borderRadius: '12px',
                backgroundColor: '#fafafa',
                border: '1px solid #f0f0f0'
              }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', marginBottom: '6px' }}>
                  <h5 style={{ fontSize: '1.05rem', fontWeight: '900', color: theme.primary, margin: 0 }}>{g.name}</h5>
                  <span style={{ fontSize: '0.7rem', color: '#999', fontWeight: '600' }}>{g.full}</span>
                </div>
                <p style={{ fontSize: '0.85rem', color: '#333', margin: '0 0 6px 0', lineHeight: '1.5', fontWeight: '600' }}>{g.vibe}</p>
                <p style={{ fontSize: '0.8rem', color: '#666', margin: 0, lineHeight: '1.5' }}>{g.note}</p>
              </div>
            ))}
          </div>
          <p style={{ fontSize: '0.75rem', color: '#999', marginTop: '16px', marginBottom: 0, lineHeight: '1.5', fontStyle: 'italic' }}>
            Pricing and turnaround times change constantly. Always check each grader's current tier pricing before submitting.
          </p>
        </div>

        {/* CTA */}
        <div style={{
          backgroundColor: theme.primaryBg,
          border: `1px solid ${theme.primaryBorder}`,
          borderRadius: '12px',
          padding: '24px',
          maxWidth: '900px',
          margin: '0 auto',
          textAlign: 'center'
        }}>
          <p style={{ fontSize: '1rem', color: theme.primary, fontWeight: '700', margin: '0 0 12px 0' }}>
            Bring your cards in and we'll walk you through it
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'center' }}>
            <a href="tel:+19093219555" style={{
              fontSize: '1rem', fontWeight: '700', color: theme.primary, textDecoration: 'none'
            }}>
              (909) 321-9555
            </a>
            <a href="mailto:info@pokepinecabin.com" style={{
              fontSize: '0.9rem', fontWeight: '600', color: theme.primary, textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '6px', justifyContent: 'center'
            }}>
              <Mail size={15} /> info@pokepinecabin.com
            </a>
          </div>
        </div>
      </div>
    </PageWrapper>
  );
}

// ─── Buy/Sell Page ────────────────────────────────────────
function BuySellPage({ isMobile }) {
  return (
    <PageWrapper isMobile={isMobile}>
      <div style={{ marginBottom: '64px' }}>
        <SectionHeader title="Buy / Sell" subtitle="We buy collections and offer consignment for sellers" />
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
          gap: '24px',
          maxWidth: '900px',
          margin: '0 auto'
        }}>
          {/* Buying */}
          <div style={{
            backgroundColor: '#ffffff',
            borderRadius: '16px',
            border: '1px solid #eee',
            padding: '28px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
              <div style={{
                width: '40px', height: '40px', borderRadius: '10px',
                backgroundColor: '#e8f5e9', display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}>
                <ShoppingBag size={20} color="#2e7d32" />
              </div>
              <h3 style={{ fontSize: '1.15rem', fontWeight: '800', color: '#1a1a1a', margin: 0 }}>We Buy</h3>
            </div>
            <p style={{ fontSize: '0.9rem', color: '#333', lineHeight: '1.7', marginBottom: '16px' }}>
              We buy Pokemon cards, collections, plushies, figures, sealed product, and collectible inventory. Whether it is a shoebox of old cards or an entire collection you are looking to move, we are interested.
            </p>
            <ul style={{ margin: 0, padding: '0 0 0 20px', fontSize: '0.85rem', color: '#555', lineHeight: '2' }}>
              <li>Single cards and bulk lots</li>
              <li>Vintage and modern collections</li>
              <li>Sealed booster boxes, ETBs, and tins</li>
              <li>Plushies, figures, and merchandise</li>
              <li>Old collections and estate lots</li>
            </ul>
            <div style={{
              backgroundColor: '#e8f5e9', borderRadius: '8px',
              padding: '12px 16px', marginTop: '20px', textAlign: 'center'
            }}>
              <p style={{ fontSize: '0.85rem', color: '#2e7d32', fontWeight: '600', margin: 0 }}>
                Call or visit the store for a quote
              </p>
            </div>
          </div>

          {/* Selling / Consignment */}
          <div style={{
            backgroundColor: '#ffffff',
            borderRadius: '16px',
            border: '1px solid #eee',
            padding: '28px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
              <div style={{
                width: '40px', height: '40px', borderRadius: '10px',
                backgroundColor: theme.primaryBg, display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}>
                <ShoppingBag size={20} color={theme.primary} />
              </div>
              <h3 style={{ fontSize: '1.15rem', fontWeight: '800', color: '#1a1a1a', margin: 0 }}>Consignment</h3>
            </div>
            <p style={{ fontSize: '0.9rem', color: '#333', lineHeight: '1.7', marginBottom: '16px' }}>
              Want to sell your graded cards or qualifying merchandise but do not want to deal with online marketplaces? We offer consignment. You leave your items at the store, we display and sell them, and when they sell you get paid.
            </p>
            <ul style={{ margin: 0, padding: '0 0 0 20px', fontSize: '0.85rem', color: '#555', lineHeight: '2' }}>
              <li>Graded cards (PSA, CGC, BGS)</li>
              <li>Qualifying merchandise and collectibles</li>
              <li>Your items displayed in-store</li>
              <li>You get paid when they sell</li>
            </ul>
            <div style={{
              backgroundColor: theme.primaryBg, borderRadius: '8px',
              padding: '12px 16px', marginTop: '20px', textAlign: 'center'
            }}>
              <p style={{ fontSize: '0.85rem', color: theme.primary, fontWeight: '600', margin: 0 }}>
                Call to set up pricing and details
              </p>
              <a href="tel:+19093219555" style={{
                fontSize: '0.95rem', fontWeight: '800', color: theme.primary, textDecoration: 'none'
              }}>
                (909) 321-9555
              </a>
            </div>
          </div>
        </div>
      </div>
    </PageWrapper>
  );
}

// ─── Summer Camp Signup Modal ─────────────────────────────
function SummerCampSignupModal({ onClose, isMobile, waitlist = false }) {
  const [form, setForm] = useState({
    parentFirst: '',
    parentLast: '',
    phone: '',
    email: '',
    notes: '',
  });
  const [children, setChildren] = useState([{ name: '', age: '' }]);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const updateChild = (i, field, value) => {
    setChildren(prev => prev.map((c, idx) => idx === i ? { ...c, [field]: value } : c));
  };

  const addChild = () => setChildren(prev => [...prev, { name: '', age: '' }]);
  const removeChild = (i) => setChildren(prev => prev.length > 1 ? prev.filter((_, idx) => idx !== i) : prev);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    const filledChildren = children.filter(c => c.name.trim());
    const { error: insertErr } = await supabase
      .from('summer_camp_signups')
      .insert({
        parent_first_name: form.parentFirst.trim(),
        parent_last_name: form.parentLast.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        children: filledChildren,
        notes: form.notes.trim() || null,
        is_waitlist: waitlist,
      });
    setSubmitting(false);
    if (insertErr) {
      setError('Something went wrong. Please try again or call us at (909) 321-9555.');
    } else {
      setSubmitted(true);
    }
  };

  const inputStyle = {
    width: '100%',
    padding: '12px 14px',
    fontSize: '16px',
    border: '1px solid #ddd',
    borderRadius: '8px',
    backgroundColor: '#fafafa',
    boxSizing: 'border-box',
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)',
        zIndex: 10000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
        overflowY: 'auto', padding: isMobile ? '20px 12px' : '40px 20px',
        backdropFilter: 'blur(3px)',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          backgroundColor: '#fff',
          border: `2px solid ${theme.primary}`,
          borderRadius: '16px',
          padding: isMobile ? '24px 20px' : '36px',
          maxWidth: '640px',
          width: '100%',
          position: 'relative',
          boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
          marginTop: isMobile ? '20px' : '40px',
          marginBottom: '40px',
        }}
      >
        <button
          type="button"
          onClick={onClose}
          style={{
            position: 'absolute', top: '14px', right: '14px',
            width: '36px', height: '36px', borderRadius: '50%',
            border: 'none', backgroundColor: '#f0f0f0', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
          aria-label="Close"
        >
          <X size={18} />
        </button>

        <h3 style={{ fontSize: '1.4rem', fontWeight: '900', color: theme.primary, margin: '0 0 6px 0' }}>
          {waitlist ? 'Join the Waiting List' : 'Reserve Your Spot'}
        </h3>
        <p style={{ fontSize: '0.9rem', color: '#666', margin: '0 0 22px 0' }}>
          {waitlist
            ? "Leave your info and we'll email you as soon as the next camp is announced."
            : "Spots are limited. Leave us your info and we'll be in touch with scheduling details."}
        </p>

        {submitted ? (
          <div style={{
            backgroundColor: '#f0fdf4',
            border: '1px solid #bbf7d0',
            borderRadius: '10px',
            padding: '24px',
            textAlign: 'center',
          }}>
            <p style={{ fontSize: '1.15rem', color: '#15803d', fontWeight: '800', margin: '0 0 8px 0' }}>
              {waitlist ? "You're on the waiting list!" : "You're on the list!"}
            </p>
            <p style={{ fontSize: '0.9rem', color: '#166534', margin: '0 0 18px 0' }}>
              {waitlist
                ? "We'll email you as soon as the next camp is announced."
                : "We'll reach out soon to confirm your spot."}
            </p>
            <button onClick={onClose} style={{
              padding: '10px 22px', backgroundColor: theme.primary, color: '#fff',
              border: 'none', borderRadius: '8px', fontSize: '0.9rem', fontWeight: '700', cursor: 'pointer',
            }}>Close</button>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '14px', marginBottom: '14px' }}>
              <div>
                <label style={{ fontSize: '0.8rem', color: '#666', fontWeight: '600', display: 'block', marginBottom: '6px' }}>Parent First Name *</label>
                <input required type="text" style={inputStyle} value={form.parentFirst} onChange={e => setForm({ ...form, parentFirst: e.target.value })} />
              </div>
              <div>
                <label style={{ fontSize: '0.8rem', color: '#666', fontWeight: '600', display: 'block', marginBottom: '6px' }}>Parent Last Name *</label>
                <input required type="text" style={inputStyle} value={form.parentLast} onChange={e => setForm({ ...form, parentLast: e.target.value })} />
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '14px', marginBottom: '20px' }}>
              <div>
                <label style={{ fontSize: '0.8rem', color: '#666', fontWeight: '600', display: 'block', marginBottom: '6px' }}>Phone *</label>
                <input required type="tel" style={inputStyle} value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} />
              </div>
              <div>
                <label style={{ fontSize: '0.8rem', color: '#666', fontWeight: '600', display: 'block', marginBottom: '6px' }}>Email *</label>
                <input required type="email" style={inputStyle} value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
              </div>
            </div>

            <label style={{ fontSize: '0.8rem', color: '#666', fontWeight: '600', display: 'block', marginBottom: '8px' }}>Children Attending</label>
            {children.map((c, i) => (
              <div key={i} style={{ display: 'grid', gridTemplateColumns: isMobile ? '2fr 1fr auto' : '3fr 1fr auto', gap: '10px', marginBottom: '10px', alignItems: 'center' }}>
                <input type="text" placeholder="Child's name" style={inputStyle} value={c.name} onChange={e => updateChild(i, 'name', e.target.value)} />
                <input type="number" min="0" max="99" placeholder="Age" style={inputStyle} value={c.age} onChange={e => updateChild(i, 'age', e.target.value)} />
                <button type="button" onClick={() => removeChild(i)} disabled={children.length === 1} style={{
                  padding: '10px 14px', backgroundColor: children.length === 1 ? '#f0f0f0' : '#fee',
                  color: children.length === 1 ? '#ccc' : theme.primary,
                  border: 'none', borderRadius: '8px', fontSize: '0.85rem', fontWeight: '600',
                  cursor: children.length === 1 ? 'not-allowed' : 'pointer'
                }}>Remove</button>
              </div>
            ))}
            <button type="button" onClick={addChild} style={{
              padding: '8px 14px', background: 'none', border: `1px dashed ${theme.primary}`,
              borderRadius: '8px', color: theme.primary, fontSize: '0.85rem', fontWeight: '600', cursor: 'pointer', marginBottom: '20px'
            }}>
              + Add another child
            </button>

            <div style={{ marginBottom: '24px' }}>
              <label style={{ fontSize: '0.8rem', color: '#666', fontWeight: '600', display: 'block', marginBottom: '6px' }}>Anything we should know? (optional)</label>
              <textarea rows="3" style={{ ...inputStyle, resize: 'vertical' }} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} />
            </div>

            {error && <p style={{ color: theme.primary, fontSize: '0.9rem', marginBottom: '14px' }}>{error}</p>}

            <button type="submit" disabled={submitting} style={{
              width: '100%', padding: '16px', backgroundColor: theme.primary, color: '#fff',
              border: 'none', borderRadius: '10px', fontSize: '1rem', fontWeight: '800',
              cursor: submitting ? 'wait' : 'pointer', letterSpacing: '0.5px'
            }}>
              {submitting ? 'Submitting...' : (waitlist ? 'Join Waiting List' : 'Reserve Our Spot')}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

// ─── Summer Camp Post-Camp View (shown after CAMP_END_DATE) ──
function SummerCampPostCamp({ isMobile, signupOpen, setSignupOpen }) {
  return (
    <PageWrapper isMobile={isMobile}>
      <div style={{ marginBottom: '48px' }}>
        {/* Hero */}
        <div className="summer-camp-banner" style={{
          borderRadius: '24px',
          padding: isMobile ? '40px 24px' : '56px 48px',
          marginBottom: '32px',
          border: `2px solid ${theme.primary}`,
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden',
        }}>
          <Sparkles style={{ position: 'absolute', top: '16px', left: isMobile ? '16px' : '40px', color: theme.primary, opacity: 0.35 }} size={20} />
          <Leaf style={{ position: 'absolute', top: '32px', right: isMobile ? '20px' : '60px', color: '#4a7c59', opacity: 0.45, transform: 'rotate(25deg)' }} size={26} />
          <Cloud style={{ position: 'absolute', bottom: '20px', left: isMobile ? '20px' : '80px', color: '#6b88b0', opacity: 0.35 }} size={24} />
          <Feather style={{ position: 'absolute', bottom: '28px', right: isMobile ? '18px' : '56px', color: theme.primary, opacity: 0.4, transform: 'rotate(-15deg)' }} size={22} />

          <div style={{
            width: isMobile ? '72px' : '96px',
            height: isMobile ? '72px' : '96px',
            borderRadius: '50%',
            backgroundColor: '#fff',
            margin: '0 auto 20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 8px 24px rgba(250,200,50,0.35)',
            border: `3px solid ${theme.primary}`,
          }}>
            <Award size={isMobile ? 40 : 56} color={theme.primary} strokeWidth={2.2} />
          </div>

          <p style={{ fontSize: '0.75rem', color: theme.primary, fontWeight: '800', letterSpacing: '3px', textTransform: 'uppercase', margin: '0 0 8px 0' }}>
            Summer 2026 · Complete
          </p>
          <h1 style={{
            fontSize: isMobile ? '1.9rem' : '2.8rem',
            fontWeight: '900',
            color: '#1a1a1a',
            margin: '0 0 12px 0',
            letterSpacing: '-0.02em',
            lineHeight: '1.1',
          }}>
            Next Camp<br/>Coming Soon
          </h1>
          <p style={{ fontSize: isMobile ? '1rem' : '1.15rem', color: '#333', lineHeight: '1.6', margin: '0 auto 22px', maxWidth: '580px' }}>
            The Summer Adventure Journal Club wrapped August 7, 2026. Want to be first to hear about next year's camp? Join the waiting list and we'll email you as soon as it's announced.
          </p>
          <button
            type="button"
            onClick={() => setSignupOpen(true)}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '8px',
              padding: isMobile ? '14px 28px' : '16px 36px',
              backgroundColor: theme.primary, color: '#fff',
              border: 'none', borderRadius: '999px',
              fontSize: isMobile ? '0.95rem' : '1rem', fontWeight: '800',
              letterSpacing: '0.5px', cursor: 'pointer',
              boxShadow: '0 6px 20px rgba(200,16,46,0.3)',
            }}
          >
            <Mail size={16} /> Join the Waiting List
          </button>
        </div>

        {/* Recap */}
        <div style={{
          backgroundColor: '#ffffff',
          border: '1px solid #eee',
          borderRadius: '14px',
          padding: isMobile ? '24px 20px' : '32px',
          marginBottom: '24px',
        }}>
          <h3 style={{ fontSize: '1.25rem', fontWeight: '800', color: theme.primary, margin: '0 0 18px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Sparkles size={20} /> How It Went
          </h3>
          <p style={{ fontSize: '1rem', color: '#333', lineHeight: '1.8', margin: '0 0 14px 0' }}>
            What a summer. From June through August, kids rolled into the cabin with open journals and open imaginations. Every week brought new themes, new prompts, and new stories — tucked into pages that went home in hands we hope keep opening them.
          </p>
          <p style={{ fontSize: '1rem', color: '#333', lineHeight: '1.8', margin: '0 0 14px 0' }}>
            We watched quiet kids find their voice. We watched loud kids find their focus. We watched siblings argue, friends collaborate, and strangers become regulars. The crafts, the stickers, the stories — all of it was the point. The journal was just the excuse to unplug for an hour.
          </p>
          <p style={{ fontSize: '1rem', color: '#333', lineHeight: '1.8', margin: 0 }}>
            Huge thank-you to every family who trusted us with their kids this summer. This was our first year running the program and it exceeded every hope we had for it. We can't wait to do it again.
          </p>
        </div>

        {/* Why join the waitlist */}
        <div style={{
          backgroundColor: theme.primaryBg,
          border: `1px solid ${theme.primaryBorder}`,
          borderRadius: '12px',
          padding: '20px 24px',
          marginBottom: '32px',
        }}>
          <p style={{ fontSize: '0.75rem', color: theme.primary, fontWeight: '800', letterSpacing: '2px', textTransform: 'uppercase', margin: '0 0 8px 0' }}>
            Missed Out This Year?
          </p>
          <p style={{ fontSize: '1rem', color: '#1a1a1a', lineHeight: '1.6', margin: 0 }}>
            Join the waiting list to be emailed when next camp opens. Waitlist families get first pick of spots before we announce publicly.
          </p>
        </div>

        {/* Bottom CTA */}
        <div style={{
          background: `linear-gradient(135deg, ${theme.primary} 0%, ${theme.primaryDark} 100%)`,
          color: '#fff',
          borderRadius: '14px',
          padding: isMobile ? '28px 20px' : '32px 28px',
          textAlign: 'center',
        }}>
          <p style={{ fontSize: isMobile ? '1.1rem' : '1.25rem', fontWeight: '800', margin: '0 0 18px 0', lineHeight: '1.4' }}>
            Be first to know when the next camp is announced.
          </p>
          <button
            type="button"
            onClick={() => setSignupOpen(true)}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '8px',
              backgroundColor: '#fff', color: theme.primary,
              padding: '12px 28px', borderRadius: '999px',
              fontSize: '0.95rem', fontWeight: '800', border: 'none', cursor: 'pointer',
            }}
          >
            <Mail size={16} /> Join the Waiting List
          </button>
        </div>
      </div>

      {signupOpen && <SummerCampSignupModal onClose={() => setSignupOpen(false)} isMobile={isMobile} waitlist={true} />}
    </PageWrapper>
  );
}

// ─── Summer Camp Page ─────────────────────────────────────
function SummerCampPage({ isMobile }) {
  const [signupOpen, setSignupOpen] = useState(false);
  const postCamp = isPostCamp();

  if (postCamp) return <SummerCampPostCamp isMobile={isMobile} signupOpen={signupOpen} setSignupOpen={setSignupOpen} />;

  const CTAButton = ({ label = 'Reserve Your Spot' }) => (
    <button
      type="button"
      onClick={() => setSignupOpen(true)}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: '8px',
        padding: isMobile ? '14px 28px' : '16px 36px',
        backgroundColor: theme.primary, color: '#fff',
        border: 'none', borderRadius: '999px',
        fontSize: isMobile ? '0.95rem' : '1rem', fontWeight: '800',
        letterSpacing: '0.5px', cursor: 'pointer',
        boxShadow: '0 6px 20px rgba(200,16,46,0.3)',
        transition: 'transform 0.2s, box-shadow 0.2s',
      }}
      onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 10px 28px rgba(200,16,46,0.45)'; }}
      onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 6px 20px rgba(200,16,46,0.3)'; }}
    >
      <Sparkles size={16} /> {label}
    </button>
  );

  return (
    <PageWrapper isMobile={isMobile}>
      <div style={{ marginBottom: '48px' }}>
        {/* Animated Hero */}
        <div className="summer-camp-banner" style={{
          borderRadius: '24px',
          padding: isMobile ? '40px 24px' : '56px 48px',
          marginBottom: '32px',
          border: `2px solid ${theme.primary}`,
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden',
        }}>
          {/* Floating decorative icons */}
          <Sparkles style={{ position: 'absolute', top: '16px', left: isMobile ? '16px' : '40px', color: theme.primary, opacity: 0.35 }} size={20} />
          <Leaf style={{ position: 'absolute', top: '32px', right: isMobile ? '20px' : '60px', color: '#4a7c59', opacity: 0.45, transform: 'rotate(25deg)' }} size={26} />
          <Cloud style={{ position: 'absolute', bottom: '20px', left: isMobile ? '20px' : '80px', color: '#6b88b0', opacity: 0.35 }} size={24} />
          <Feather style={{ position: 'absolute', bottom: '28px', right: isMobile ? '18px' : '56px', color: theme.primary, opacity: 0.4, transform: 'rotate(-15deg)' }} size={22} />

          <div style={{
            width: isMobile ? '72px' : '96px',
            height: isMobile ? '72px' : '96px',
            borderRadius: '50%',
            backgroundColor: '#fff',
            margin: '0 auto 20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 8px 24px rgba(250,200,50,0.35)',
            border: `3px solid ${theme.primary}`,
          }}>
            <Sun size={isMobile ? 40 : 56} color="#f59e0b" strokeWidth={2.2} />
          </div>

          <p style={{ fontSize: '0.75rem', color: theme.primary, fontWeight: '800', letterSpacing: '3px', textTransform: 'uppercase', margin: '0 0 8px 0' }}>
            Summer 2026
          </p>
          <h1 style={{
            fontSize: isMobile ? '2rem' : '3rem',
            fontWeight: '900',
            color: '#1a1a1a',
            margin: '0 0 12px 0',
            letterSpacing: '-0.02em',
            lineHeight: '1.1',
          }}>
            Summer Adventure <br/>Journal Club
          </h1>
          <p style={{ fontSize: isMobile ? '1rem' : '1.15rem', color: '#333', lineHeight: '1.6', margin: '0 auto 18px', maxWidth: '560px' }}>
            Unplug, get outside, and explore creativity this summer. A relaxed journaling experience for kids of all ages.
          </p>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            backgroundColor: theme.primary,
            color: '#fff',
            padding: '10px 22px',
            borderRadius: '999px',
            fontSize: '0.95rem',
            fontWeight: '800',
            letterSpacing: '0.5px',
            marginBottom: '24px',
          }}>
            <CalendarIcon size={16} /> June 1 – August 7, 2026
          </div>
          <div>
            <CTAButton label="Reserve Your Spot" />
          </div>
        </div>

        {/* Quick facts grid — with icons */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)',
          gap: '14px',
          marginBottom: '32px',
        }}>
          {[
            { icon: CalendarIcon, label: 'Duration', value: 'All Summer Long' },
            { icon: Users, label: 'Ages', value: 'All Ages Welcome' },
            { icon: Clock, label: 'Schedule', value: '1 hr · Twice a Week' },
            { icon: DollarSign, label: 'Price', value: '$30 per person' },
          ].map((item, i) => {
            const Icon = item.icon;
            return (
              <div key={i} style={{
                backgroundColor: '#ffffff',
                border: '1px solid #eee',
                borderRadius: '14px',
                padding: '20px 14px',
                textAlign: 'center',
                transition: 'transform 0.2s, box-shadow 0.2s',
              }}>
                <div style={{
                  width: '44px',
                  height: '44px',
                  borderRadius: '50%',
                  backgroundColor: theme.primaryBg,
                  margin: '0 auto 10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  <Icon size={22} color={theme.primary} />
                </div>
                <p style={{ fontSize: '0.7rem', color: theme.primary, fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1px', margin: '0 0 4px 0' }}>{item.label}</p>
                <p style={{ fontSize: '0.95rem', color: '#1a1a1a', fontWeight: '700', margin: 0 }}>{item.value}</p>
              </div>
            );
          })}
        </div>

        {/* Location callout */}
        <div style={{
          backgroundColor: theme.primaryBg,
          border: `1px solid ${theme.primaryBorder}`,
          borderRadius: '12px',
          padding: '16px 20px',
          marginBottom: '32px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '10px',
        }}>
          <MapPin size={18} color={theme.primary} />
          <p style={{ fontSize: '0.95rem', color: theme.primary, fontWeight: '700', margin: 0 }}>
            Held at Poke Pine Cabin · Indoor area: Poke-lounge
          </p>
        </div>

        {/* What to Expect + Weekly Themes */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
          gap: '20px',
          marginBottom: '32px',
        }}>
          <div style={{ backgroundColor: '#ffffff', border: '1px solid #eee', borderRadius: '14px', padding: '24px' }}>
            <h3 style={{ fontSize: '1.15rem', fontWeight: '800', color: theme.primary, margin: '0 0 16px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <BookOpen size={20} /> What to Expect
            </h3>
            <ul style={{ listStyle: 'none', margin: 0, padding: 0, fontSize: '0.9rem', color: '#555' }}>
              {[
                { icon: PencilLine, text: 'Fun and creative journaling prompts' },
                { icon: Palette, text: 'Drawing, writing, and decorating indoors' },
                { icon: Sparkles, text: 'Stickers, crafts, and creative supplies' },
                { icon: Heart, text: 'A relaxed, no-pressure environment' },
                { icon: BookOpen, text: 'Make your own personalized journal' },
              ].map((item, i) => {
                const Icon = item.icon;
                return (
                  <li key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '8px 0', borderBottom: i < 4 ? '1px solid #f5f5f5' : 'none' }}>
                    <Icon size={16} color={theme.primary} style={{ flexShrink: 0, marginTop: '2px' }} />
                    <span>{item.text}</span>
                  </li>
                );
              })}
            </ul>
          </div>
          <div style={{ backgroundColor: '#ffffff', border: '1px solid #eee', borderRadius: '14px', padding: '24px' }}>
            <h3 style={{ fontSize: '1.15rem', fontWeight: '800', color: theme.primary, margin: '0 0 16px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Sparkles size={20} /> Weekly Themes
            </h3>
            <ul style={{ listStyle: 'none', margin: 0, padding: 0, fontSize: '0.9rem', color: '#555' }}>
              {[
                { icon: Sun, text: '"My Summer Adventures"' },
                { icon: TreePine, text: '"Nature Discoveries"' },
                { icon: Star, text: '"Favorite Memories"' },
                { icon: Feather, text: '"Creative Storytelling"' },
                { icon: MapIcon, text: '"Design Your Own World"' },
              ].map((item, i) => {
                const Icon = item.icon;
                return (
                  <li key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '8px 0', borderBottom: i < 4 ? '1px solid #f5f5f5' : 'none' }}>
                    <Icon size={16} color={theme.primary} style={{ flexShrink: 0, marginTop: '2px' }} />
                    <span>{item.text}</span>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>

        {/* Why Join */}
        <div style={{ backgroundColor: '#ffffff', border: '1px solid #eee', borderRadius: '14px', padding: '24px', marginBottom: '24px' }}>
          <h3 style={{ fontSize: '1.15rem', fontWeight: '800', color: theme.primary, margin: '0 0 16px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Heart size={20} /> Why Join?
          </h3>
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
            gap: '10px',
            fontSize: '0.9rem',
            color: '#555',
          }}>
            {[
              { icon: Sparkles, text: 'Boost creativity and imagination' },
              { icon: TreePine, text: 'Enjoy fresh air and outdoor fun' },
              { icon: Star, text: 'Build confidence in self-expression' },
              { icon: Smile, text: 'Make new friends' },
              { icon: Heart, text: 'Create something meaningful to keep' },
            ].map((item, i) => {
              const Icon = item.icon;
              return (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 14px', backgroundColor: theme.primaryBg, borderRadius: '8px' }}>
                  <Icon size={16} color={theme.primary} style={{ flexShrink: 0 }} />
                  <span>{item.text}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Bonus */}
        <div style={{
          background: `linear-gradient(135deg, ${theme.primary} 0%, ${theme.primaryDark} 100%)`,
          color: '#fff',
          borderRadius: '14px',
          padding: '24px',
          marginBottom: '40px',
          textAlign: 'center',
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '18px',
        }}>
          <div style={{
            width: '56px', height: '56px', borderRadius: '50%',
            backgroundColor: 'rgba(255,255,255,0.2)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}>
            <Award size={30} color="#fff" />
          </div>
          <div style={{ textAlign: isMobile ? 'center' : 'left' }}>
            <p style={{ fontSize: '0.75rem', fontWeight: '700', letterSpacing: '2px', textTransform: 'uppercase', opacity: 0.9, margin: '0 0 4px 0' }}>Bonus</p>
            <p style={{ fontSize: '1.1rem', fontWeight: '800', margin: 0 }}>End-of-Summer Showcase + Special Certificate</p>
          </div>
        </div>

        {/* Bottom CTA */}
        <div style={{ textAlign: 'center', padding: '8px 0 32px' }}>
          <p style={{ fontSize: '1.05rem', color: theme.primary, fontWeight: '800', margin: '0 0 14px 0' }}>
            Spots are Limited — Sign Up Today!
          </p>
          <CTAButton label="Reserve Our Spot" />
        </div>

        {/* Contact + Calendar link */}
        <div style={{
          backgroundColor: '#fafafa',
          border: '1px solid #eee',
          borderRadius: '14px',
          padding: '24px',
          textAlign: 'center',
        }}>
          <p style={{ fontSize: '0.9rem', color: '#666', margin: '0 0 14px 0' }}>
            Questions? Call (909) 321-9555 or email pokepinecabin@gmail.com
          </p>
          <Link to="/calendar" style={{
            display: 'inline-flex', alignItems: 'center', gap: '6px',
            color: theme.primary, fontSize: '0.95rem', fontWeight: '700',
            textDecoration: 'none', borderBottom: `2px solid ${theme.primary}`, paddingBottom: '2px'
          }}>
            See all our events on the calendar →
          </Link>
        </div>
      </div>

      {signupOpen && <SummerCampSignupModal onClose={() => setSignupOpen(false)} isMobile={isMobile} />}
    </PageWrapper>
  );
}

// ─── Homeschool 3D Printing Page ──────────────────────────
function HomeschoolPrintingPage({ isMobile }) {
  return (
    <PageWrapper isMobile={isMobile}>
      <div style={{ marginBottom: '48px' }}>
        {/* Hero */}
        <div className="summer-camp-banner" style={{
          borderRadius: '24px',
          padding: isMobile ? '40px 24px' : '56px 48px',
          marginBottom: '32px',
          border: `2px solid ${theme.primary}`,
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden',
        }}>
          <Sparkles style={{ position: 'absolute', top: '16px', left: isMobile ? '16px' : '40px', color: theme.primary, opacity: 0.35 }} size={20} />
          <Boxes style={{ position: 'absolute', top: '32px', right: isMobile ? '20px' : '60px', color: '#4a7c59', opacity: 0.45, transform: 'rotate(15deg)' }} size={26} />
          <Lightbulb style={{ position: 'absolute', bottom: '20px', left: isMobile ? '20px' : '80px', color: '#f59e0b', opacity: 0.4 }} size={24} />
          <Wrench style={{ position: 'absolute', bottom: '28px', right: isMobile ? '18px' : '56px', color: theme.primary, opacity: 0.4, transform: 'rotate(-15deg)' }} size={22} />

          <div style={{
            width: isMobile ? '72px' : '96px',
            height: isMobile ? '72px' : '96px',
            borderRadius: '50%',
            backgroundColor: '#fff',
            margin: '0 auto 20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 8px 24px rgba(200,16,46,0.25)',
            border: `3px solid ${theme.primary}`,
          }}>
            <Printer size={isMobile ? 40 : 56} color={theme.primary} strokeWidth={2.2} />
          </div>

          <p style={{ fontSize: '0.75rem', color: theme.primary, fontWeight: '800', letterSpacing: '3px', textTransform: 'uppercase', margin: '0 0 8px 0' }}>
            Daily Homeschool Program
          </p>
          <h1 style={{
            fontSize: isMobile ? '1.9rem' : '2.8rem',
            fontWeight: '900',
            color: '#1a1a1a',
            margin: '0 0 12px 0',
            letterSpacing: '-0.02em',
            lineHeight: '1.1',
          }}>
            Homeschool 3D<br/>Printing School
          </h1>
          <p style={{ fontSize: isMobile ? '1rem' : '1.15rem', color: '#333', lineHeight: '1.6', margin: '0 auto 18px', maxWidth: '620px' }}>
            A hands-on STEM program for creative kids. Learn filament 3D printing, beginner design, and real-world engineering skills in a fun, supportive environment.
          </p>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            backgroundColor: theme.primary,
            color: '#fff',
            padding: '10px 22px',
            borderRadius: '999px',
            fontSize: '0.95rem',
            fontWeight: '800',
            letterSpacing: '0.5px',
          }}>
            <CalendarIcon size={16} /> Monday – Friday · Daytime Hours
          </div>
        </div>

        {/* Quick facts grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)',
          gap: '14px',
          marginBottom: '32px',
        }}>
          {[
            { icon: CalendarIcon, label: 'Schedule', value: 'Mon – Fri' },
            { icon: Users, label: 'Ages', value: '8 – 16' },
            { icon: GraduationCap, label: 'Experience', value: 'Beginners Welcome' },
            { icon: MapPin, label: 'Location', value: 'Crestline, CA' },
          ].map((item, i) => {
            const Icon = item.icon;
            return (
              <div key={i} style={{
                backgroundColor: '#ffffff',
                border: '1px solid #eee',
                borderRadius: '14px',
                padding: '20px 14px',
                textAlign: 'center',
              }}>
                <div style={{
                  width: '44px',
                  height: '44px',
                  borderRadius: '50%',
                  backgroundColor: theme.primaryBg,
                  margin: '0 auto 10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  <Icon size={22} color={theme.primary} />
                </div>
                <p style={{ fontSize: '0.7rem', color: theme.primary, fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1px', margin: '0 0 4px 0' }}>{item.label}</p>
                <p style={{ fontSize: '0.95rem', color: '#1a1a1a', fontWeight: '700', margin: 0 }}>{item.value}</p>
              </div>
            );
          })}
        </div>

        {/* What Kids Will Learn + Weekly Projects */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
          gap: '20px',
          marginBottom: '32px',
        }}>
          <div style={{ backgroundColor: '#ffffff', border: '1px solid #eee', borderRadius: '14px', padding: '24px' }}>
            <h3 style={{ fontSize: '1.15rem', fontWeight: '800', color: theme.primary, margin: '0 0 16px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <GraduationCap size={20} /> What Kids Will Learn
            </h3>
            <ul style={{ listStyle: 'none', margin: 0, padding: 0, fontSize: '0.9rem', color: '#555' }}>
              {[
                { icon: Printer, text: 'How filament 3D printers work' },
                { icon: Palette, text: '3D design with beginner software (Tinkercad)' },
                { icon: Wrench, text: 'Slicing and preparing files for printing' },
                { icon: Lightbulb, text: 'Print setup, troubleshooting, and printer care' },
                { icon: BookOpen, text: 'STEM fundamentals through real projects' },
              ].map((item, i) => {
                const Icon = item.icon;
                return (
                  <li key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '8px 0', borderBottom: i < 4 ? '1px solid #f5f5f5' : 'none' }}>
                    <Icon size={16} color={theme.primary} style={{ flexShrink: 0, marginTop: '2px' }} />
                    <span>{item.text}</span>
                  </li>
                );
              })}
            </ul>
          </div>
          <div style={{ backgroundColor: '#ffffff', border: '1px solid #eee', borderRadius: '14px', padding: '24px' }}>
            <h3 style={{ fontSize: '1.15rem', fontWeight: '800', color: theme.primary, margin: '0 0 16px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Sparkles size={20} /> Weekly Projects
            </h3>
            <ul style={{ listStyle: 'none', margin: 0, padding: 0, fontSize: '0.9rem', color: '#555' }}>
              {[
                { icon: Star, text: 'Keychains and custom name tags' },
                { icon: ShoppingBag, text: 'Pokemon card holders and display stands' },
                { icon: Boxes, text: 'Mini storage boxes and desk organizers' },
                { icon: Heart, text: 'Mystery builds and creative challenges' },
                { icon: Wrench, text: 'Multi-part prints and beginner engineering designs' },
              ].map((item, i) => {
                const Icon = item.icon;
                return (
                  <li key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '8px 0', borderBottom: i < 4 ? '1px solid #f5f5f5' : 'none' }}>
                    <Icon size={16} color={theme.primary} style={{ flexShrink: 0, marginTop: '2px' }} />
                    <span>{item.text}</span>
                  </li>
                );
              })}
            </ul>
            <p style={{ fontSize: '0.85rem', color: '#777', fontStyle: 'italic', margin: '16px 0 0 0', textAlign: 'center' }}>
              Kids design, print, and take home their creations.
            </p>
          </div>
        </div>

        {/* Program Schedule callout */}
        <div style={{
          backgroundColor: theme.primaryBg,
          border: `1px solid ${theme.primaryBorder}`,
          borderRadius: '12px',
          padding: '20px 24px',
          marginBottom: '32px',
          textAlign: 'center',
        }}>
          <p style={{ fontSize: '0.75rem', color: theme.primary, fontWeight: '800', letterSpacing: '2px', textTransform: 'uppercase', margin: '0 0 8px 0' }}>
            Program Schedule
          </p>
          <p style={{ fontSize: '1rem', color: '#1a1a1a', fontWeight: '700', margin: '0 0 4px 0' }}>
            Monday – Friday · Daytime Homeschool Hours
          </p>
          <p style={{ fontSize: '0.9rem', color: '#555', margin: 0 }}>
            Hosted at The Poke Pine Cabin in Crestline, CA. Join for consistent weekly learning or attend multiple days depending on availability.
          </p>
        </div>

        {/* Spots Limited CTA */}
        <div style={{
          background: `linear-gradient(135deg, ${theme.primary} 0%, ${theme.primaryDark} 100%)`,
          color: '#fff',
          borderRadius: '14px',
          padding: isMobile ? '28px 20px' : '32px 28px',
          textAlign: 'center',
        }}>
          <p style={{ fontSize: '0.75rem', fontWeight: '700', letterSpacing: '2px', textTransform: 'uppercase', opacity: 0.9, margin: '0 0 10px 0' }}>
            Spots Are Limited
          </p>
          <p style={{ fontSize: isMobile ? '1.1rem' : '1.25rem', fontWeight: '800', margin: '0 0 18px 0', lineHeight: '1.4' }}>
            Class sizes are kept small so every student gets hands-on time. New students can join anytime.
          </p>
          <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: '12px', justifyContent: 'center', alignItems: 'center' }}>
            <a href="tel:+19093219555" style={{
              display: 'inline-flex', alignItems: 'center', gap: '8px',
              backgroundColor: '#fff', color: theme.primary,
              padding: '12px 24px', borderRadius: '999px',
              fontSize: '0.95rem', fontWeight: '800', textDecoration: 'none',
            }}>
              <Phone size={16} /> (909) 321-9555
            </a>
            <a href="mailto:pokepinecabin@gmail.com" style={{
              display: 'inline-flex', alignItems: 'center', gap: '8px',
              backgroundColor: 'rgba(255,255,255,0.15)', color: '#fff',
              padding: '12px 24px', borderRadius: '999px',
              fontSize: '0.95rem', fontWeight: '800', textDecoration: 'none',
              border: '1px solid rgba(255,255,255,0.3)',
            }}>
              <Mail size={16} /> Email Us
            </a>
          </div>
        </div>
      </div>
    </PageWrapper>
  );
}

// ─── Calendar Page ────────────────────────────────────────
function CalendarPage({ isMobile, isAdmin, staff }) {
  const [activeFilter, setActiveFilter] = useState(null);
  const [events, setEvents] = useState([]);
  const calendarRef = useRef(null);

  const fetchEvents = useCallback(async () => {
    const { data } = await supabase
      .from('events')
      .select('*')
      .order('start_time', { ascending: true });
    setEvents(data || []);
  }, []);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  // Derive weekly schedule from recurring weekly events
  const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const DAY_ORDER = [0, 2, 3, 4, 5, 6]; // Sun, Tue, Wed, Thu, Fri, Sat (skip Mon)
  const weeklyEvents = events
    .filter(ev => ev.recurrence === 'weekly')
    .map(ev => {
      const evDate = new Date(ev.event_date + 'T00:00:00');
      const dow = evDate.getDay();
      return { ...ev, dow, dayName: DAY_NAMES[dow] };
    })
    .sort((a, b) => DAY_ORDER.indexOf(a.dow) - DAY_ORDER.indexOf(b.dow));

  // Derive special events (vendor days, big events)
  const specialEvents = events.filter(ev => ev.category === 'big_event' && ev.recurrence === 'none');

  // Get today's events
  const today = new Date();
  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  const todayEvents = events.filter(ev => {
    if (ev.event_date === todayStr) return true;
    if (ev.recurrence === 'none') return false;
    const evDate = new Date(ev.event_date + 'T00:00:00');
    if (today < evDate) return false;
    if (ev.recurrence_end_date && today > new Date(ev.recurrence_end_date + 'T00:00:00')) return false;
    const diffDays = Math.floor((today - evDate) / (1000 * 60 * 60 * 24));
    if (ev.recurrence === 'weekly') return diffDays % 7 === 0;
    if (ev.recurrence === 'biweekly') return diffDays % 14 === 0;
    if (ev.recurrence === 'monthly') return evDate.getDate() === today.getDate();
    return false;
  });

  // If nothing today, find next day with events (up to 7 days ahead)
  let nextDayEvents = [];
  let nextDayLabel = '';
  if (todayEvents.length === 0) {
    for (let offset = 1; offset <= 7; offset++) {
      const checkDate = new Date(today);
      checkDate.setDate(checkDate.getDate() + offset);
      const checkStr = `${checkDate.getFullYear()}-${String(checkDate.getMonth() + 1).padStart(2, '0')}-${String(checkDate.getDate()).padStart(2, '0')}`;
      const found = events.filter(ev => {
        if (ev.event_date === checkStr) return true;
        if (ev.recurrence === 'none') return false;
        const evDate = new Date(ev.event_date + 'T00:00:00');
        if (checkDate < evDate) return false;
        if (ev.recurrence_end_date && checkDate > new Date(ev.recurrence_end_date + 'T00:00:00')) return false;
        const diffDays = Math.floor((checkDate - evDate) / (1000 * 60 * 60 * 24));
        if (ev.recurrence === 'weekly') return diffDays % 7 === 0;
        if (ev.recurrence === 'biweekly') return diffDays % 14 === 0;
        if (ev.recurrence === 'monthly') return evDate.getDate() === checkDate.getDate();
        return false;
      });
      if (found.length > 0) {
        nextDayEvents = found;
        nextDayLabel = offset === 1 ? 'Tomorrow' : DAY_NAMES[checkDate.getDay()];
        break;
      }
    }
  }

  const formatTime = formatTime12h;

  const FILTER_OPTIONS = [
    { key: null, label: 'All Events' },
    { key: 'trade_night', label: 'Trade Night', color: theme.primary },
    { key: 'big_event', label: 'Vendor Day', color: '#7c3aed' },
    { key: 'tournament', label: 'Tournament', color: '#2563eb' },
    { key: 'other', label: 'Weekly Events', color: '#ea580c' },
  ];

  const handlePrint = () => {
    if (!calendarRef.current) return;
    const printWin = window.open('', '_blank');
    const calEl = calendarRef.current;
    const calDate = calEl.dataset.year && calEl.dataset.month
      ? new Date(parseInt(calEl.dataset.year), parseInt(calEl.dataset.month))
      : new Date();
    const yr = calDate.getFullYear();
    const mo = calDate.getMonth();
    const moName = calDate.toLocaleString('default', { month: 'long' });
    const firstDay = new Date(yr, mo, 1).getDay();
    const daysInMo = new Date(yr, mo + 1, 0).getDate();

    // Build day-to-events map from actual data
    const getEventsForPrintDay = (d) => {
      const dateObj = new Date(yr, mo, d);
      const dateStr = `${yr}-${String(mo + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      return events.filter(ev => {
        if (ev.event_date === dateStr) return true;
        if (ev.recurrence === 'none') return false;
        const evDate = new Date(ev.event_date + 'T00:00:00');
        if (dateObj < evDate) return false;
        if (ev.recurrence_end_date && dateObj > new Date(ev.recurrence_end_date + 'T00:00:00')) return false;
        const diffDays = Math.floor((dateObj - evDate) / (1000 * 60 * 60 * 24));
        if (ev.recurrence === 'weekly') return diffDays % 7 === 0;
        if (ev.recurrence === 'biweekly') return diffDays % 14 === 0;
        if (ev.recurrence === 'monthly') return evDate.getDate() === d;
        return false;
      });
    };

    printWin.document.write(`
      <html><head><title>Poke Pine Cabin - ${moName} ${yr}</title>
      <style>
        @page { size: landscape; margin: 0.5in; }
        body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; margin: 0; padding: 20px; }
        .header { display: flex; align-items: center; gap: 16px; margin-bottom: 24px; padding-bottom: 16px; border-bottom: 2px solid ${theme.primary}; }
        .header img { width: 50px; height: 50px; border-radius: 10px; }
        .header h1 { margin: 0; font-size: 22px; color: #1a1a1a; }
        .header span { color: #888; font-size: 14px; }
        .grid { display: grid; grid-template-columns: repeat(7, 1fr); gap: 0; border: 1px solid #ddd; }
        .day-header { background: #1a1a1a; color: #fff; padding: 8px; text-align: center; font-weight: 700; font-size: 12px; }
        .day-cell { border: 1px solid #eee; padding: 8px; min-height: 80px; font-size: 11px; vertical-align: top; }
        .day-num { font-weight: 700; font-size: 14px; margin-bottom: 4px; }
        .event-title { background: #f5f5f5; border-radius: 4px; padding: 2px 6px; margin: 2px 0; font-size: 10px; font-weight: 600; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .event-title.trade_night { border-left: 3px solid ${theme.primary}; }
        .event-title.big_event { border-left: 3px solid #7c3aed; }
        .event-title.tournament { border-left: 3px solid #2563eb; }
        .event-title.other { border-left: 3px solid #ea580c; }
        .empty { background: #fafafa; }
        .weekly { margin-top: 20px; display: flex; gap: 16px; flex-wrap: wrap; font-size: 11px; color: #666; }
        .weekly strong { color: #1a1a1a; }
      </style></head><body>
      <div class="header">
        <div><h1>Poke Pine Cabin - ${moName} ${yr}</h1><span>23872 Lake Dr Unit B-C, Crestline, CA 92325 | (909) 321-9555</span></div>
      </div>
    `);

    printWin.document.write('<div class="grid">');
    ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].forEach(d => {
      printWin.document.write('<div class="day-header">' + d + '</div>');
    });
    for (let i = 0; i < firstDay; i++) printWin.document.write('<div class="day-cell empty"></div>');

    for (let d = 1; d <= daysInMo; d++) {
      const dayEvts = getEventsForPrintDay(d);
      const evHtml = dayEvts.map(ev => '<div class="event-title ' + (ev.category || 'other') + '">' + ev.title + '</div>').join('');
      printWin.document.write('<div class="day-cell"><div class="day-num">' + d + '</div>' + evHtml + '</div>');
    }
    const totalCells = firstDay + daysInMo;
    const remaining = totalCells % 7 === 0 ? 0 : 7 - (totalCells % 7);
    for (let i = 0; i < remaining; i++) printWin.document.write('<div class="day-cell empty"></div>');
    printWin.document.write('</div>');

    // Dynamic weekly legend from DB
    printWin.document.write('<div class="weekly">');
    weeklyEvents.forEach(ev => {
      const shortDay = ev.dayName.slice(0, 3);
      printWin.document.write('<span><strong>' + shortDay + ':</strong> ' + ev.title + '</span>');
    });
    if (specialEvents.length > 0) {
      printWin.document.write('<span><strong>Special:</strong> ' + specialEvents[0].title + ' (see calendar)</span>');
    }
    printWin.document.write('</div>');

    printWin.document.write('</body></html>');
    printWin.document.close();
    setTimeout(() => printWin.print(), 300);
  };

  return (
    <PageWrapper isMobile={isMobile}>
      <div style={{ position: 'relative' }}>
        <SectionHeader title="Calendar" subtitle="Upcoming events and activities" />

        {/* What's Happening Today / Next Up */}
        {(todayEvents.length > 0 || nextDayEvents.length > 0) && (() => {
          const STORE_HOURS_CAL = { 0: [11.5, 18], 1: null, 2: null, 3: null, 4: [12.5, 18], 5: [12.5, 19], 6: [11.5, 19] };
          const nowH = STORE_HOURS_CAL[today.getDay()];
          const currentHour = today.getHours() + today.getMinutes() / 60;
          const isOpen = nowH && currentHour >= nowH[0] && currentHour < nowH[1];
          const hasToday = todayEvents.length > 0;
          const headerLabel = hasToday ? "What's Happening Today" : `Next Up: ${nextDayLabel}`;
          const headerColor = hasToday ? theme.primary : '#2563eb';
          const bgColor = hasToday ? theme.primaryBg : '#f0f7ff';
          const borderColor = hasToday ? `1px solid ${theme.primaryBorder}` : '1px solid #dbeafe';
          return (
          <div style={{
            backgroundColor: bgColor,
            border: borderColor,
            borderRadius: '12px',
            padding: '16px 20px',
            marginBottom: '20px',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
              <p style={{
                fontSize: '0.75rem', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '0.5px',
                color: headerColor, margin: 0
              }}>
                {headerLabel}
              </p>
              <div style={{
                display: 'flex', alignItems: 'center', gap: '6px',
                padding: '3px 10px', borderRadius: '20px',
                backgroundColor: isOpen ? '#f0fdf4' : '#fef2f2',
                border: isOpen ? '1px solid #bbf7d0' : '1px solid #fecaca',
              }}>
                <div style={{
                  width: '7px', height: '7px', borderRadius: '50%',
                  backgroundColor: isOpen ? '#22c55e' : '#ef4444',
                  boxShadow: isOpen ? '0 0 4px rgba(34,197,94,0.5)' : 'none',
                }} />
                <span style={{
                  fontSize: '0.7rem', fontWeight: '700',
                  color: isOpen ? '#166534' : '#991b1b',
                }}>
                  {isOpen ? 'Open Now' : 'Currently Closed'}
                </span>
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {(todayEvents.length > 0 ? todayEvents : nextDayEvents).map((ev, i) => (
                <div key={i} style={{
                  display: 'flex', alignItems: 'center', gap: '10px',
                }}>
                  <div style={{
                    width: '8px', height: '8px', borderRadius: '50%', flexShrink: 0,
                    backgroundColor: CATEGORIES[ev.category]?.color || '#ea580c'
                  }} />
                  <div>
                    <span style={{ fontSize: '0.9rem', fontWeight: '700', color: '#1a1a1a' }}>{ev.title}</span>
                    {ev.start_time && (
                      <span style={{ fontSize: '0.8rem', color: '#888', marginLeft: '8px' }}>
                        {formatTime(ev.start_time)}{ev.end_time ? ` - ${formatTime(ev.end_time)}` : ''}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
          );
        })()}

        {/* Weekly Schedule Cards (dynamic from DB) */}
        {weeklyEvents.length > 0 && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)',
            gap: '10px',
            marginBottom: '20px'
          }}>
            {weeklyEvents.map(ev => (
              <div key={ev.id} style={{
                padding: '14px 16px', borderRadius: '10px', backgroundColor: '#ffffff',
                border: '1px solid #eee',
                borderLeft: '3px solid ' + (CATEGORIES[ev.category]?.color || '#ea580c'),
              }}>
                <p style={{ fontSize: '0.7rem', color: '#999', fontWeight: '700', margin: '0 0 2px 0', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{ev.dayName}</p>
                <h4 style={{ fontSize: '0.85rem', fontWeight: '800', color: '#1a1a1a', margin: '0 0 2px 0' }}>{ev.title}</h4>
                {ev.description && (
                  <p style={{ fontSize: '0.7rem', color: '#888', margin: 0, lineHeight: '1.3' }}>
                    {ev.description.length > 80 ? ev.description.slice(0, 80) + '...' : ev.description}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Special Events callout (dynamic) */}
        {specialEvents.length > 0 && (
          <div style={{
            padding: '14px 20px', borderRadius: '10px', backgroundColor: '#f5f3ff',
            border: '1px solid #e9e5ff', marginBottom: '20px',
            display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap'
          }}>
            <span style={{ fontSize: '0.8rem', fontWeight: '800', color: '#7c3aed' }}>{specialEvents[0].title}</span>
            {specialEvents[0].description && (
              <span style={{ fontSize: '0.75rem', color: '#888' }}>
                {specialEvents[0].description.length > 100 ? specialEvents[0].description.slice(0, 100) + '...' : specialEvents[0].description}
              </span>
            )}
          </div>
        )}

        {/* Filter pills + Print button */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px', flexWrap: 'wrap'
        }}>
          {FILTER_OPTIONS.map(opt => (
            <button
              key={opt.key || 'all'}
              onClick={() => setActiveFilter(activeFilter === opt.key ? null : opt.key)}
              style={{
                padding: '6px 14px', borderRadius: '20px', fontSize: '0.75rem', fontWeight: '700',
                cursor: 'pointer', transition: 'all 0.15s',
                border: activeFilter === opt.key ? '2px solid ' + (opt.color || '#1a1a1a') : '2px solid #e0e0e0',
                backgroundColor: activeFilter === opt.key ? (opt.color || '#1a1a1a') : '#fff',
                color: activeFilter === opt.key ? '#fff' : '#666',
              }}
            >
              {opt.label}
            </button>
          ))}
          <div style={{ flex: 1 }} />
          <button
            onClick={handlePrint}
            style={{
              padding: '6px 14px', borderRadius: '20px', fontSize: '0.75rem', fontWeight: '700',
              cursor: 'pointer', border: '2px solid #e0e0e0', backgroundColor: '#fff', color: '#666',
            }}
          >
            Print Month
          </button>
        </div>

        <div ref={calendarRef} data-year="" data-month="" style={{
          position: 'relative',
          backgroundColor: '#ffffff',
          borderRadius: '16px',
          overflow: 'hidden'
        }}>
          <img
            src="/logo-transparent.png"
            alt=""
            style={{
              position: 'absolute',
              bottom: '20px',
              left: '50%',
              transform: 'translateX(-50%)',
              width: '420px',
              opacity: 0.08,
              pointerEvents: 'none',
              zIndex: 1
            }}
          />
          <div style={{ position: 'relative', zIndex: 2 }}>
            <Calendar isStaff={isAdmin} isMobile={isMobile} staff={staff} categoryFilter={activeFilter} calendarRef={calendarRef} events={events} fetchEvents={fetchEvents} />
          </div>
        </div>
      </div>
    </PageWrapper>
  );
}

// ─── Blog List Page ──────────────────────────────────────
const BLOG_RELEASE_DATES = [
  'Mar 31', 'Apr 7', 'Apr 14', 'Apr 21', 'Apr 28', 'May 5',
  'May 12', 'May 19', 'May 26', 'Jun 2', 'Jun 9', 'Jun 16'
];

function BlogListPage({ isMobile }) {
  return (
    <PageWrapper isMobile={isMobile}>
      <div style={{ marginBottom: '64px' }}>
        <SectionHeader title="Blog" subtitle="Tips, guides, and everything Pokemon" />
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>
          {BLOG_DATA.map((blog, i) => {
            const isPublished = blog.published;
            const releaseDate = BLOG_RELEASE_DATES[i] || '';

            const card = (
              <div style={{
                backgroundColor: isPublished ? '#ffffff' : '#fafafa',
                borderRadius: '12px',
                border: '1px solid #eee',
                padding: '20px 24px',
                marginBottom: '12px',
                display: 'flex',
                alignItems: isMobile ? 'flex-start' : 'center',
                flexDirection: isMobile ? 'column' : 'row',
                gap: isMobile ? '8px' : '16px',
                transition: isPublished ? 'transform 0.2s, box-shadow 0.2s' : 'none',
                cursor: isPublished ? 'pointer' : 'default',
                opacity: isPublished ? 1 : 0.55
              }}
              onMouseEnter={isPublished ? e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.08)'; } : undefined}
              onMouseLeave={isPublished ? e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; } : undefined}
              >
                <h3 style={{ fontSize: '0.95rem', fontWeight: '700', color: isPublished ? '#1a1a1a' : '#999', margin: 0, flex: 1 }}>
                  {blog.title}
                </h3>
                {!isPublished && (
                  <span style={{
                    fontSize: '0.7rem', fontWeight: '700', color: theme.primary,
                    backgroundColor: theme.primaryBg, padding: '4px 10px', borderRadius: '6px',
                    whiteSpace: 'nowrap'
                  }}>
                    Coming {releaseDate}
                  </span>
                )}
              </div>
            );

            if (isPublished) {
              return (
                <Link key={blog.slug} to={`/blog/${blog.slug}`} style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}>
                  {card}
                </Link>
              );
            }
            return <div key={blog.slug}>{card}</div>;
          })}
        </div>
      </div>
    </PageWrapper>
  );
}

// ─── Blog Post Page ──────────────────────────────────────
function BlogPostPage({ isMobile }) {
  const { slug } = useParams();
  const blog = BLOG_DATA.find(b => b.slug === slug);

  if (!blog || !blog.published) {
    return (
      <PageWrapper isMobile={isMobile}>
        <div style={{ maxWidth: '800px', margin: '0 auto', padding: '40px 20px', textAlign: 'center' }}>
          <h2 style={{ fontSize: '1.5rem', fontWeight: '700', color: '#1a1a1a', marginBottom: '16px' }}>Post not found</h2>
          <Link to="/blog" style={{ color: theme.primary, textDecoration: 'none', fontWeight: '600' }}>Back to Blog</Link>
        </div>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper isMobile={isMobile}>
      <div style={{ maxWidth: '800px', margin: '0 auto', padding: '40px 20px 64px' }}>
        <Link to="/blog" style={{ color: theme.primary, textDecoration: 'none', fontWeight: '600', fontSize: '0.9rem', display: 'inline-block', marginBottom: '24px' }}>
          &larr; Back to Blog
        </Link>
        <h1 style={{ fontSize: isMobile ? '1.5rem' : '2rem', fontWeight: '800', color: '#1a1a1a', lineHeight: 1.3, marginBottom: '32px' }}>
          {blog.title}
        </h1>
        {blog.content.map((block, i) => {
          if (block.type === 'h2') {
            return <h2 key={i} style={{ fontSize: '1.3rem', fontWeight: '700', color: '#1a1a1a', marginTop: '32px', marginBottom: '12px', lineHeight: 1.3 }}>{block.text}</h2>;
          }
          if (block.type === 'h3') {
            return <h3 key={i} style={{ fontSize: '1.1rem', fontWeight: '700', color: '#1a1a1a', marginTop: '24px', marginBottom: '8px', lineHeight: 1.3 }}>{block.text}</h3>;
          }
          if (block.type === 'p') {
            return <p key={i} style={{ fontSize: '1rem', color: '#333', lineHeight: 1.75, marginBottom: '16px' }} dangerouslySetInnerHTML={{ __html: block.text }} />;
          }
          if (block.type === 'li') {
            return (
              <ul key={i} style={{ paddingLeft: '24px', marginBottom: '16px' }}>
                {block.items.map((item, j) => (
                  <li key={j} style={{ fontSize: '1rem', color: '#333', lineHeight: 1.75, marginBottom: '8px' }}>{item}</li>
                ))}
              </ul>
            );
          }
          return null;
        })}
      </div>
    </PageWrapper>
  );
}

// ─── Nav Link Helper ──────────────────────────────────────
const NAV_ITEMS = [
  { label: 'Home', to: '/' },
  { label: 'Calendar', to: '/calendar' },
  { label: 'Visit Us', to: '/#visit-us' },
  { label: 'Buy/Sell', to: '/buy-sell' },
  { label: 'Consultation', to: '/consultation' },
  { label: 'Grading', to: '/grading' },
  { label: 'Classes', to: '/homeschool-3d-printing' },
  { label: 'Camps', to: '/summer-camp' },
  { label: 'Blog', to: '/blog' }
];

// ─── Main App ─────────────────────────────────────────────
function App() {
  const [navVisible, setNavVisible] = useState(false);
  const [staffUser, setStaffUser] = useState(null);
  const [staffProfile, setStaffProfile] = useState(null);
  const profileFetchRef = useRef(null);
  const isAdmin = !!staffProfile?.is_admin;
  // Compact "current staff" object passed down to the calendar so the
  // EventModal can stamp created_by / updated_by on saves.
  const staff = staffUser?.id && staffProfile
    ? { id: staffUser.id, name: staffProfile.name, isAdmin }
    : null;
  const [showLogin, setShowLogin] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const location = useLocation();

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Show nav immediately on non-home pages, use scroll behavior on home page
  useEffect(() => {
    if (location.pathname !== '/') {
      setNavVisible(true);
      return;
    }
    const handleScroll = () => setNavVisible(window.scrollY > 100);
    // Reset on home page
    setNavVisible(window.scrollY > 100);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [location.pathname]);

  // Handle hash scrolling for /#visit-us
  useEffect(() => {
    if (location.hash === '#visit-us') {
      setTimeout(() => {
        const el = document.getElementById('visit-us');
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  }, [location]);

  // Check for existing staff session
  // Per AUTH_PLAYBOOK: only set state in here, NO database calls.
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setStaffUser(session?.user || null);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_, session) => {
      setStaffUser(session?.user || null);
      if (!session) setStaffProfile(null);
    });
    return () => subscription.unsubscribe();
  }, []);

  // Fetch staff profile in a SEPARATE effect, watching staffUser?.id.
  // Source of truth for is_admin lives in public.profiles, not the JWT,
  // so admin changes take effect on next fetch with no re-login required.
  useEffect(() => {
    if (!staffUser?.id) {
      setStaffProfile(null);
      return;
    }

    if (profileFetchRef.current) profileFetchRef.current.cancelled = true;
    const fetchState = { cancelled: false };
    profileFetchRef.current = fetchState;

    const timeoutPromise = new Promise((_, reject) =>
      setTimeout(() => reject(new Error('Profile fetch timeout')), 5000)
    );

    Promise.race([
      supabase.from('profiles').select('*').eq('id', staffUser.id).single(),
      timeoutPromise
    ])
      .then(({ data, error } = {}) => {
        if (fetchState.cancelled) return;
        if (error) {
          console.error('Profile fetch error:', error.message);
          setStaffProfile(null);
        } else {
          setStaffProfile(data);
        }
      })
      .catch((err) => {
        if (!fetchState.cancelled) console.error('Profile fetch failed:', err.message);
      });

    return () => { fetchState.cancelled = true; };
  }, [staffUser?.id]);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    setStaffUser(null);
    setStaffProfile(null);
  };

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#f8f8f8',
      color: '#1a1a1a',
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif"
    }}>
      <ScrollToTop />

      {/* Nav - hidden until scroll on home, always visible on other pages */}
      <nav style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1000,
        backgroundColor: '#ffffff',
        borderBottom: '1px solid #eee',
        padding: '0 24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        height: '64px',
        boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
        transform: navVisible ? 'translateY(0)' : 'translateY(-100%)',
        transition: 'transform 0.35s ease-in-out'
      }}>
        <Link to="/" style={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
          <img src="/full-logo.jpg" alt="Poke Pine Cabin" style={{ height: '48px', width: 'auto', objectFit: 'contain' }} />
        </Link>
        <div style={{ display: 'flex', gap: '28px', alignItems: 'center' }}>
          {/* Desktop nav links */}
          {!isMobile && NAV_ITEMS.map(item => (
            item.label === 'Visit Us' ? (
              <Link
                key={item.label}
                to={item.to}
                style={{
                  color: '#555',
                  textDecoration: 'none',
                  fontSize: '0.85rem',
                  fontWeight: '600',
                  transition: 'color 0.2s'
                }}
                onMouseEnter={e => e.target.style.color = theme.primary}
                onMouseLeave={e => e.target.style.color = '#555'}
              >
                {item.label}
              </Link>
            ) : (
              <Link
                key={item.label}
                to={item.to}
                style={{
                  color: location.pathname === item.to ? theme.primary : '#555',
                  textDecoration: 'none',
                  fontSize: '0.85rem',
                  fontWeight: '600',
                  transition: 'color 0.2s'
                }}
                onMouseEnter={e => e.target.style.color = theme.primary}
                onMouseLeave={e => { if (location.pathname !== item.to) e.target.style.color = '#555'; }}
              >
                {item.label}
              </Link>
            )
          ))}
          {/* Staff lock icon - always visible */}
          <button
            onClick={() => staffUser ? handleLogout() : setShowLogin(true)}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontSize: '1rem',
              color: staffUser ? theme.primary : '#1a1a1a',
              padding: '4px',
              transition: 'color 0.2s'
            }}
            title={staffUser ? 'Staff: Logged in (click to logout)' : 'Staff Login'}
          >
            {staffUser ? <Unlock size={20} /> : <Lock size={20} />}
          </button>
          {/* Hamburger menu button - mobile only */}
          {isMobile && (
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: '#1a1a1a',
                padding: '4px',
                display: 'flex',
                alignItems: 'center'
              }}
            >
              {menuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          )}
        </div>
      </nav>

      {/* Mobile dropdown menu */}
      {isMobile && menuOpen && navVisible && (
        <div style={{
          position: 'fixed',
          top: '64px',
          left: 0,
          right: 0,
          zIndex: 999,
          backgroundColor: '#ffffff',
          borderBottom: '1px solid #eee',
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
          display: 'flex',
          flexDirection: 'column',
          padding: '8px 0'
        }}>
          {NAV_ITEMS.map(item => (
            <Link
              key={item.label}
              to={item.to}
              onClick={() => setMenuOpen(false)}
              style={{
                color: location.pathname === item.to ? theme.primary : '#555',
                textDecoration: 'none',
                fontSize: '0.95rem',
                fontWeight: '600',
                padding: '14px 24px',
                borderBottom: '1px solid #f0f0f0',
                transition: 'background-color 0.2s, color 0.2s'
              }}
              onMouseEnter={e => { e.target.style.backgroundColor = theme.primaryBg; e.target.style.color = theme.primary; }}
              onMouseLeave={e => { e.target.style.backgroundColor = 'transparent'; if (location.pathname !== item.to) e.target.style.color = '#555'; }}
            >
              {item.label}
            </Link>
          ))}
        </div>
      )}

      {/* Routes */}
      <Routes>
        <Route path="/" element={<HomePage isMobile={isMobile} />} />
        <Route path="/consultation" element={<ConsultationPage isMobile={isMobile} />} />
        <Route path="/grading" element={<GradingPage isMobile={isMobile} />} />
        <Route path="/buy-sell" element={<BuySellPage isMobile={isMobile} />} />
        <Route path="/calendar" element={<CalendarPage isMobile={isMobile} isAdmin={isAdmin} staff={staff} />} />
        <Route path="/summer-camp" element={<SummerCampPage isMobile={isMobile} />} />
        <Route path="/homeschool-3d-printing" element={<HomeschoolPrintingPage isMobile={isMobile} />} />
        <Route path="/blog" element={<BlogListPage isMobile={isMobile} />} />
        <Route path="/blog/:slug" element={<BlogPostPage isMobile={isMobile} />} />
      </Routes>

      {/* Staff Login Modal */}
      {showLogin && (
        <StaffLogin
          onClose={() => setShowLogin(false)}
          onLogin={() => { /* auth listener handles setStaffUser */ }}
        />
      )}

      {/* Staff banner */}
      {isAdmin && (
        <div style={{
          position: 'fixed', bottom: '16px', right: '16px', zIndex: 999,
          backgroundColor: theme.primary, color: '#fff', padding: '8px 16px',
          borderRadius: '8px', fontSize: '0.75rem', fontWeight: '700',
          boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
        }}>
          Staff Mode - Click calendar days to add events
        </div>
      )}

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Russo+One&display=swap');
        @keyframes slideIn {
          from { opacity: 0; transform: translateX(20px); }
          to { opacity: 1; transform: translateX(0); }
        }
        @keyframes marquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-25%); }
        }
        html { scroll-behavior: smooth; }
        * { box-sizing: border-box; }
        body { margin: 0; }
        @media (max-width: 768px) {
          body { -webkit-text-size-adjust: 100%; }
        }
      `}</style>
    </div>
  );
}

export default App;
